<script lang="ts">
const DEFAULT_EVENT_TITLE = '未命名日程'
const MAX_HEADER_ROWS = 4
const MONTH_EVENT_ROW_HEIGHT = 24
const MONTH_MORE_BTN_HEIGHT = 20
const EVENT_LIST_PANEL_WIDTH = 260
const EVENT_LIST_PANEL_HEIGHT = 380
const EVENT_LIST_PANEL_MARGIN = 12
const EVENT_LIST_PANEL_GAP = 8
</script>
<template>
  <div
    ref="calendarContainerRef"
    :class="[bem.b(), editPopoverOpen ? bem.is('editing') : '']"
    :style="panelStyle"
    @click.capture="handleContainerClickCapture"
    @contextmenu.prevent
    @mousedown.capture="handleContainerPointerDown"
    @touchstart.capture="handleContainerPointerDown"
  >
    <VueCal
      ref="calendarRef"
      v-bind="vueCalAttrs"
      :class="[
        bem.e('panel'),
        `ll-calendar__panel--text-${props.eventTextSize}`,
        currentViewId === 'week' ? 'll-calendar__panel--view-week' : '',
        isTransientScrollbarVisible ? 'll-calendar__panel--scrolling' : '',
      ]"
      :view="currentViewId"
      :view-date="resolvedCurrentDate"
      :events="calendarEventsForView"
      :time-from="timeFromMinutes"
      :time-to="timeToMinutes"
      :time-step="slotStepMinutes"
      :time-cell-height="props.timeCellHeight"
      :start-week-on-sunday="weekStartsOnSunday"
      :event-create-min-drag="15"
      :editable-events="vueCalEditableEvents"
      @cell-click="handleCellClick"
      @event-click="handleNativeEventClick"
      @event-contextmenu="handleEventContextmenu"
      @event-resize-end="handleEventDurationChange"
      @event-dropped="handleEventDrop"
      @event-created="handleEventDragCreate"
      @view-change="handleViewChange"
      @ready="handleReady"
    >
      <template #header="data">
        <slot name="toolbar" :data="data" />
        <div :class="bem.m('header-spacer')">
          <a-space :size="16" :class="bem.m('header-controls')">
            <ll-button size="small" @click="() => handleGoToToday(data.view)">
              今日
            </ll-button>
            <ll-icon-base
              :is-show-tooltip="false"
              icon-name="icon-left"
              @click="data.view.previous"
            />
            <ll-icon-base
              :is-show-tooltip="false"
              icon-name="icon-right"
              @click="data.view.next"
            />
            <ll-date-picker
              :class="bem.m('header-date-picker')"
              is-gray
              :value="headerPickerValue"
              :picker="headerPickerType"
              :format="headerPickerFormat"
              :allow-clear="false"
              @update:value="handleHeaderDateChange"
            />
          </a-space>

          <ll-segmented
            v-model:value="currentViewId"
            checked-style="while"
            :options="
              Object.keys(data.availableViews).map((e) => ({
                value: e,
                title: titleEnum[e],
              }))
            "
            size="small"
            use-hover
          />
        </div>
        <div
          v-if="currentViewId === 'day'"
          class="ll-calendar__day-header-strip"
        >
          <div
            class="ll-calendar__day-header-timezone"
            :style="dayHeaderTimezoneStyle"
          >
            {{ dayHeaderTimezoneLabel }}
            <ll-icon-base
              v-if="dayHeaderEvents.length > MAX_HEADER_ROWS"
              class="ll-calendar__header-expand-btn"
              :is-show-tooltip="false"
              :icon-name="
                headerEventsExpanded
                  ? 'icon-line-arrow-folding'
                  : 'icon-line-arrow-down'
              "
              @click.stop="headerEventsExpanded = !headerEventsExpanded"
            />
          </div>

          <div
            class="ll-calendar__weekday-crossday-area ll-calendar__day-header-list"
            @click.stop="
              !readonly &&
              canQuickCreate &&
              handleWeekHeadingCreateClick(
                internalCurrentDate,
                $event as MouseEvent,
              )
            "
          >
            <div
              v-for="(event, rowIdx) in headerEventsExpanded
                ? dayHeaderEvents
                : dayHeaderEvents.slice(0, MAX_HEADER_ROWS)"
              :key="`${getEventIdentifier(event)}-${rowIdx}`"
              :class="[
                'll-calendar__weekday-crossday-row',
                `ll-calendar__weekday-crossday-row--single`,
                !readonly
                  ? 'll-calendar__weekday-crossday-row--interactive'
                  : '',
                getEventIdentifier(event) === newDraftEventIdRef
                  ? 'is-new'
                  : '',
              ]"
              :style="getCrossDayRowStyle(event, 'single')"
              @click.stop="
                !readonly && openEditPopover(event, '', $event as MouseEvent)
              "
              @contextmenu.prevent.stop="
                !readonly && openContextMenu(event, $event as MouseEvent)
              "
            >
              <span class="ll-calendar__weekday-crossday-title">
                <span
                  v-if="event.tag"
                  class="ll-calendar__event-tag"
                  :style="
                    event.tagColor ? { backgroundColor: event.tagColor } : {}
                  "
                  >{{ event.tag }}</span
                >{{ getEventTitle(event) || '未命名日程' }}
              </span>
            </div>
            <div
              v-if="
                dayHeaderEvents.length > MAX_HEADER_ROWS &&
                !headerEventsExpanded
              "
              class="ll-calendar__more-events"
              @click.stop="
                openHeaderMoreEventsModal(
                  internalCurrentDate,
                  dayHeaderEvents,
                  $event as MouseEvent,
                )
              "
            >
              还有 {{ dayHeaderEvents.length - MAX_HEADER_ROWS }} 条记录
            </div>
          </div>
          <div style="width: 30px">
            <ll-icon-base
              class="ll-calendar__day-header-lunar"
              icon-name="icon-add"
              :is-show-tooltip="false"
              @click.stop="
                !readonly &&
                canQuickCreate &&
                handleWeekHeadingCreateClick(
                  internalCurrentDate,
                  $event as MouseEvent,
                )
              "
            />
          </div>
        </div>
      </template>

      <template #weekday-heading="{ id, date }">
        <div class="ll-calendar__weekday-heading">
          <div
            :class="[
              'll-calendar__weekday-heading-title',
              !readonly && canQuickCreate && currentViewId === 'week'
                ? 'll-calendar__weekday-heading-title--clickable'
                : '',
            ]"
            @click.stop="
              !readonly &&
              canQuickCreate &&
              currentViewId === 'week' &&
              handleWeekHeadingCreateClick(date, $event as MouseEvent)
            "
          >
            <span class="vuecal__weekday-day">{{ weekdayIdToLabel(id) }}</span>
            <span v-if="currentViewId === 'week'" class="vuecal__weekday-date">
              {{ formatWeekdayDate(date)
              }}<span v-if="props.showLunar" class="ll-calendar__lunar-date">{{
                getLunarDate(date)
              }}</span>
            </span>
          </div>
          <div
            v-if="weekCrossDayLayout.totalRows > 0"
            class="ll-calendar__weekday-crossday-area"
          >
            <div
              v-for="(crossSlot, rowIdx) in getColumnCrossDayVisibleEvents(
                date,
              )"
              :key="rowIdx"
              :class="[
                'll-calendar__weekday-crossday-row',
                crossSlot
                  ? `ll-calendar__weekday-crossday-row--${getWeekHeaderEventSegment(crossSlot, date)}`
                  : 'll-calendar__weekday-crossday-row--empty',
                crossSlot && !readonly
                  ? 'll-calendar__weekday-crossday-row--interactive'
                  : '',
                isWeekCrossDayDropTarget(date)
                  ? 'll-calendar__weekday-crossday-row--drop-target'
                  : '',
                crossSlot && isWeekCrossDayDragging(crossSlot.event)
                  ? 'll-calendar__weekday-crossday-row--dragging'
                  : '',
                crossSlot &&
                getEventIdentifier(crossSlot.event) === newDraftEventIdRef
                  ? 'is-new'
                  : '',
              ]"
              :style="
                crossSlot
                  ? getCrossDayRowStyle(
                      crossSlot.event,
                      getWeekHeaderEventSegment(crossSlot, date) as any,
                    )
                  : {}
              "
              :draggable="
                Boolean(crossSlot && canDragWeekCrossDayEvent(crossSlot.event))
              "
              @click.stop="
                crossSlot &&
                !readonly &&
                !shouldSuppressWeekCrossDayClick() &&
                openEditPopover(crossSlot.event, '', $event as MouseEvent)
              "
              @contextmenu.prevent.stop="
                crossSlot &&
                !readonly &&
                openContextMenu(crossSlot.event, $event as MouseEvent)
              "
              @dragstart="
                crossSlot &&
                handleWeekCrossDayDragStart(
                  crossSlot.event,
                  date,
                  $event as DragEvent,
                )
              "
              @dragover="handleWeekCrossDayDragOver(date, $event as DragEvent)"
              @drop="handleWeekCrossDayDrop(date, $event as DragEvent)"
              @dragend="handleWeekCrossDayDragEnd"
            >
              <span
                v-if="
                  crossSlot &&
                  (getWeekHeaderEventSegment(crossSlot, date) === 'start' ||
                    getWeekHeaderEventSegment(crossSlot, date) === 'single')
                "
                class="ll-calendar__weekday-crossday-title"
              >
                <span
                  v-if="crossSlot.event.tag"
                  class="ll-calendar__event-tag"
                  :style="
                    crossSlot.event.tagColor
                      ? { backgroundColor: crossSlot.event.tagColor }
                      : {}
                  "
                  >{{ crossSlot.event.tag }}</span
                >{{ getEventTitle(crossSlot.event) || '未命名日程' }}
              </span>
            </div>
            <!-- 当任意列有隐藏事件时，每列均渲染该 div以保持高度一致；无隐藏事件的列用 visibility:hidden 占位 -->
            <div
              v-if="weekCrossDayHasMore && !headerEventsExpanded"
              class="ll-calendar__more-events"
              :class="{
                'll-calendar__more-events--placeholder':
                  getColumnCrossDayHiddenCount(date) === 0,
              }"
              @click.stop="
                getColumnCrossDayHiddenCount(date) > 0 &&
                openHeaderMoreEventsModal(
                  date,
                  getColumnCrossDayAllEvents(date),
                  $event as MouseEvent,
                )
              "
            >
              还有 {{ getColumnCrossDayHiddenCount(date) }} 条记录
            </div>
          </div>
        </div>
      </template>

      <template v-if="currentViewId === 'month'" #cell="{ cell }">
        <div
          class="ll-calendar__month-cell-inner"
          :class="{
            'll-calendar__month-cell-inner--dragover':
              canDragMonthEvent &&
              monthDragOverCellDate &&
              isSameDayCell(monthDragOverCellDate, cell.start),
          }"
          @dragenter="
            canDragMonthEvent &&
            handleMonthCellDragEnter(cell.start, $event as DragEvent)
          "
          @dragover="
            canDragMonthEvent &&
            handleMonthCellDragOver(cell.start, $event as DragEvent)
          "
          @dragleave="
            canDragMonthEvent &&
            handleMonthCellDragLeave(cell.start, $event as DragEvent)
          "
          @drop="
            canDragMonthEvent &&
            handleMonthCellDrop(cell.start, $event as DragEvent)
          "
        >
          <ll-icon-base
            v-if="!readonly && canQuickCreate && !isMonthCellInPast(cell.start)"
            class="ll-calendar__month-cell-add-btn"
            icon-name="icon-add"
            :is-show-tooltip="false"
            :on-click="(event) => handleMonthCellCreateClick(cell, event)"
          />
          <div class="vuecal__cell-date">
            {{ formatMonthCellDate(cell.start)
            }}<span v-if="props.showLunar" class="ll-calendar__lunar-date">{{
              getLunarDate(cell.start)
            }}</span>
          </div>
          <div class="vuecal__cell-events">
            <div
              v-for="event in getMonthCellVisibleEvents(cell)"
              :key="getEventIdentifier(event)"
              :class="[
                'vuecal__event',
                getMonthCellEventClass(event, cell),
                getEventIdentifier(event) === newDraftEventIdRef
                  ? 'is-new'
                  : '',
                ,
                canDragMonthEvent &&
                monthDraggingEvent &&
                getEventIdentifier(monthDraggingEvent) ===
                  getEventIdentifier(event)
                  ? 'll-calendar__month-event--dragging'
                  : '',
              ]"
              :style="getMonthEventStyle(event)"
              :draggable="canDragMonthEvent ? 'true' : 'false'"
              @click.stop="
                handleNativeEventClick({ event, e: $event as MouseEvent })
              "
              @contextmenu="
                handleEventContextmenu({
                  event,
                  nativeEvent: $event as MouseEvent,
                })
              "
              @dragstart.stop="
                canDragMonthEvent &&
                handleMonthEventDragStart(event, $event as DragEvent)
              "
              @dragend.stop="handleMonthEventDragEnd"
            >
              <div
                :class="[
                  'll-calendar__event-content-week',
                  { 'll-calendar__event-content-week--interactive': !readonly },
                  event.class,
                ]"
              >
                <span
                  v-if="event.tag"
                  class="ll-calendar__event-tag"
                  :style="
                    event.tagColor ? { backgroundColor: event.tagColor } : {}
                  "
                  >{{ event.tag }}</span
                >{{ getEventTitle(event) || '未命名日程' }}
              </div>
            </div>
            <div
              v-if="getMonthCellHiddenEventCount(cell) > 0"
              class="ll-calendar__more-events"
              @click.stop="openMonthEventListModal(cell, $event)"
            >
              还有 {{ getMonthCellHiddenEventCount(cell) }} 条记录
            </div>
          </div>
        </div>
      </template>
      <template v-else #event="{ event }">
        <div
          class="ll-calendar__event-skin"
          :style="getTimedEventSkinStyle(event)"
        />
        <div
          :class="[
            getEventIdentifier(event) === newDraftEventIdRef ? 'is-new' : '',
            ,
            'll-calendar__event-content',
          ]"
        >
          <h6 class="ll-calendar__event-fixed-title">
            <span
              v-if="event.tag"
              class="ll-calendar__event-tag"
              :style="event.tagColor ? { backgroundColor: event.tagColor } : {}"
              >{{ event.tag }}</span
            >{{ getEventTitle(event) }}
          </h6>
          <p class="ll-calendar__event-fixed-time">
            {{ getEventTimeLabel(event) }}
          </p>
          <div class="ll-calendar__event-fields">
            <slot name="event" :event="event">
              <template
                v-for="(field, index) in resolvedEventDisplayFields"
                :key="`${field.type || 'text'}-${field.key || index}-${index}`"
              >
                <div
                  v-if="isRowDisplayField(field)"
                  class="ll-calendar__event-row"
                >
                  <a-row
                    class="ll-calendar__event-row-inner"
                    v-bind="getRowProps(field)"
                    :style="field.style"
                    v-on="field.on"
                  >
                    <a-col
                      v-for="(child, childIndex) in field.children || []"
                      :key="`${child.type || 'text'}-${child.key || childIndex}-${childIndex}`"
                      v-bind="getRowColProps(child)"
                    >
                      <component
                        :is="resolveFieldComponent(child)"
                        v-if="isComponentDisplayField(child)"
                        v-bind="getFieldProps(event, child)"
                        :style="child.style"
                        v-on="child.on"
                      />
                      <span
                        v-else
                        class="ll-calendar__event-text"
                        :style="child.style"
                        v-on="child.on"
                      >
                        {{ getEventFieldValue(event, child.key) }}
                      </span>
                    </a-col>
                  </a-row>
                </div>

                <component
                  :is="resolveFieldComponent(field)"
                  v-else-if="isComponentDisplayField(field)"
                  v-bind="getFieldProps(event, field)"
                  :style="field.style"
                  v-on="field.on"
                />

                <span
                  v-else
                  class="ll-calendar__event-text"
                  :style="field.style"
                  v-on="field.on"
                >
                  {{ getEventFieldValue(event, field.key) }}
                </span>
              </template>
            </slot>
          </div>
        </div>
      </template>
    </VueCal>

    <div
      v-if="customNowLineVisible"
      class="ll-calendar__custom-now-line"
      :style="customNowLineStyle"
      aria-hidden="true"
    >
      <span
        v-show="!customNowLineTimeHidden"
        class="ll-calendar__custom-now-line-time"
        >{{ customNowLineTime }}</span
      >
    </div>
    <div
      v-if="currentViewId === 'week'"
      class="ll-calendar__week-header-timezone"
      :style="weekHeaderTimezoneStyle"
    >
      {{ dayHeaderTimezoneLabel }}

      <ll-icon-base
        v-if="weekCrossDayHasMore"
        class="ll-calendar__header-expand-btn"
        :is-show-tooltip="false"
        :icon-name="
          headerEventsExpanded
            ? 'icon-line-arrow-folding'
            : 'icon-line-arrow-down'
        "
        @click.stop="headerEventsExpanded = !headerEventsExpanded"
      />
    </div>

    <div
      v-if="customScrollbarVisible"
      ref="customScrollbarTrackRef"
      class="ll-calendar__custom-scrollbar"
      :class="{
        'll-calendar__custom-scrollbar--visible': isTransientScrollbarVisible,
      }"
      :style="customScrollbarTrackStyle"
      aria-hidden="true"
      @mousedown="handleScrollbarTrackMousedown"
    >
      <div
        class="ll-calendar__custom-scrollbar-thumb"
        :style="customScrollbarThumbStyle"
        @mousedown.stop="handleScrollbarThumbMousedown"
      />
    </div>

    <calendar-event-list-modal
      v-model:open="eventListModalOpen"
      :title="eventListModalTitle"
      :events="selectedEventListEvents"
      :get-event-identifier="getEventIdentifier"
      :get-event-title="getEventTitle"
      :get-event-time-label="getEventTimeLabel"
      :on-select-event="handleEventListItemClick"
      :on-contextmenu-event="handleEventListItemContextmenu"
      :edit-popover-open="editPopoverOpen"
      :edit-presentation-mode="editPresentationMode"
      :edit-popover-placement="editPopoverPlacement"
      :context-menu-open="contextMenuOpen"
      :context-menu-event="contextMenuEvent"
      :context-menu-style="contextMenuStyle"
      :edit-popover-style="editPopoverStyle"
      :edit-boundary-style="editBoundaryStyle"
      :edit-form-data="editFormData"
      :edit-form-columns="editFormColumns"
      :is-creating-event="isCreatingEvent"
      :editing-event-title="editingEventTitle"
      :current-editable-event="currentEditableEvent"
      :close-context-menu="closeContextMenu"
      :handle-context-menu-edit="handleContextMenuEdit"
      :handle-context-menu-copy="handleContextMenuCopy"
      :handle-context-menu-delete="handleContextMenuDelete"
      :handle-mask-close="handleMaskClose"
      :handle-switch-presentation="handleSwitchPresentation"
      :handle-confirm-edit="handleConfirmEdit"
      :handle-confirm-and-add="handleConfirmAndAdd"
      :handle-delete-edit-event="handleDeleteEditEvent"
      :on-bind-edit-panel-ref="bindEditPanelRef"
      :on-bind-context-menu-panel-ref="bindContextMenuPanelRef"
      :panel-style="eventListPanelStyle"
    >
      <template v-if="$slots['edit-form']" #edit-form="{ data }">
        <slot name="edit-form" :data="data" />
      </template>
      <template #event-contextmenu="{ event, close, edit, copy, remove }">
        <slot
          name="event-contextmenu"
          :event="event"
          :close="close"
          :edit="edit"
          :copy="copy"
          :remove="remove"
        />
      </template>
    </calendar-event-list-modal>
  </div>
</template>

<script setup lang="ts">
import { VueCal } from 'vue-cal'
import 'vue-cal/style.css'
import { createNamespace } from '@ll-plus/utils'
import dayjs, { type Dayjs } from 'dayjs'
import {
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  useAttrs,
  toRaw,
} from 'vue'

import {
  calendarProps,
  defaultCalendarFieldComponents,
  normalizeCalendarEvent,
  normalizeCalendarEvents,
  titleEnum,
  type CalendarDateGroup,
  type CalendarEventDisplayField,
  type CalendarDatesChangeRaw,
  type CalendarEvent,
  type CalendarEventClickRaw,
  type CalendarView,
} from './calendar'
import CalendarEventListModal from './calendar-event-list-modal.vue'
import {
  buildDateGroupEvents,
  foldDateGroupEvents,
  formatEventDateTime,
  formatEventTime,
  getCrossDaySortMetrics,
  getEnabledDateGroups,
  getLunarDate,
  isCrossDayEvent,
  isEventOverlappingRange,
  isSameEvents,
  isToday,
  normalizeDateLikeValue,
  parseTimeToMinutes,
  toCalendarEvent,
  toCalendarEvents,
  toDateValue,
} from './calendar-utils'
import { useCalendarEditor } from './use-calendar-editor'

defineOptions({
  name: 'LlCalendar',
})

const props = defineProps(calendarProps)
const emit = defineEmits([
  'update:modelValue',
  'update:view',
  'change',
  'date-click',
  'select',
  'event-click',
  'event-contextmenu',
  'event-drop',
  'event-resize',
  'event-edit',
  'event-delete',
  'dates-change',
])
const attrs = useAttrs()
const bem = createNamespace('calendar')

const calendarRef = ref<any>()
const calendarContainerRef = ref<HTMLElement>()
const eventListModalOpen = ref(false)
const selectedEventListDate = ref<Date | null>(null)
const selectedEventListEvents = ref<CalendarEvent[]>([])
const eventListPanelStyle = ref<Record<string, string>>({
  position: 'fixed',
  left: '-9999px',
  top: '-9999px',
})
const eventListModalTitle = computed(() => {
  const date = selectedEventListDate.value
  if (!date) {
    return '日程列表'
  }

  const target = dayjs(date)
  if (!target.isValid()) {
    return '日程列表'
  }

  const weekLabels = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  return `${target.format('M月D日')} ${weekLabels[target.day()]}`
})
const currentEditableEvent = computed(() => {
  return {
    drag: true,
    resize: true,
    delete: true,
    create: true,
    selectCreate: true,
    ...props.editableEvents,
  }
})

const parseBooleanLike = (value: unknown, defaultValue = false) => {
  if (value === '' || value === true) {
    return true
  }

  if (value === false || value === null || value === undefined) {
    return defaultValue
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'true' || normalized === '1') {
      return true
    }
    if (normalized === 'false' || normalized === '0') {
      return false
    }
  }

  return Boolean(value)
}

const isWatchRealTimeEnabled = computed(() =>
  parseBooleanLike(
    (attrs as Record<string, unknown>)['watch-real-time'] ??
      (attrs as Record<string, unknown>).watchRealTime,
    false,
  ),
)

const vueCalAttrs = computed(() => {
  const nextAttrs: Record<string, unknown> = {
    ...(attrs as Record<string, unknown>),
    // 显式转发 VueCal 需要的 LlCalendar props
    'day-max-events': props.dayMaxEvents,
    'all-day-slot': props.allDaySlot,
    'events-on-month-view': props.eventsOnMonthView,
  }

  delete nextAttrs['watch-real-time']
  delete nextAttrs.watchRealTime

  return nextAttrs
})

const customNowLineVisible = ref(false)
const customNowLineTime = ref('')
const customNowLineTimeHidden = ref(false)
const customNowLineStyle = ref<Record<string, string>>({
  left: '-9999px',
  top: '-9999px',
  width: '0px',
  '--ll-custom-now-dot-left': '-5px',
})
const canQuickCreate = computed(
  () =>
    !props.readonly &&
    props.selectable &&
    currentEditableEvent.value.create !== false,
)
const canSelectCreate = computed(
  () =>
    !props.readonly &&
    props.selectable &&
    currentEditableEvent.value.selectCreate !== false,
)
const vueCalEditableEvents = computed(() => ({
  drag: !props.readonly && currentEditableEvent.value.drag !== false,
  resize: !props.readonly && currentEditableEvent.value.resize !== false,
  delete: !props.readonly && currentEditableEvent.value.delete !== false,
  // vue-cal 的 create 主要控制拖拽/框选创建；普通创建由 canQuickCreate 控制。
  // 月视图使用自定义 #cell 插槽，事件创建通过 + 按钮或单元格点击触发；
  // 禁用月视图的 create，避免 vue-cal 的单元格拖拽监听器拦截自定义事件拖拽
  // 并误触发 @event-created（新增日程）。
  create:
    !props.readonly && canSelectCreate.value && currentViewId.value !== 'month',
}))

// ---- 日期组（dateGroups）展开 / 折叠 ----
// 显示层与存储层分离：modelValue 保持「一条原始记录一行」的存储结构，
// 内部 internalEvents 在启用日期组时按组展开为多条派生事件用于渲染与交互。
const enabledDateGroups = computed<CalendarDateGroup[]>(() =>
  getEnabledDateGroups(props.dateGroups),
)
const isDateGroupMode = computed(() => enabledDateGroups.value.length > 0)

// 上一次的原始记录列表：用于折叠时保留未展示字段、判定记录删除。
const baseRecords = ref<CalendarEvent[]>(
  (props.modelValue ?? []).map((record) => ({ ...record })),
)

// 将原始记录列表展开为显示层事件（未启用日期组时原样返回）。
const expandRecords = (records: CalendarEvent[]): CalendarEvent[] => {
  if (!isDateGroupMode.value) {
    return records ?? []
  }
  return buildDateGroupEvents(
    records ?? [],
    enabledDateGroups.value,
    props.dateGroupTitleFormat,
  )
}

const internalEvents = ref<CalendarEvent[]>(
  toCalendarEvents(expandRecords(props.modelValue)),
)

// 记录最近一次由组件内部 commitEvents 向外 emit 的 modelValue 快照。
// modelValue 是 v-model：emit 后父级回写会同步触发下方 modelValue watch，
// 若该回写正是我们自己刚 emit 的内容（启用日期组时 fold/expand 往返可能并非
// 逐字节等价），需要直接跳过，避免 internalEvents 被反复重算而陷入递归更新。
let lastCommittedOutput: CalendarEvent[] | null = null

const normalizeCellEvents = (events: unknown): CalendarEvent[] => {
  if (!events) {
    return []
  }

  if (Array.isArray(events)) {
    return events.filter(Boolean) as CalendarEvent[]
  }

  if (events instanceof Map) {
    return Array.from(events.values()).filter(Boolean) as CalendarEvent[]
  }

  if (events instanceof Set) {
    return Array.from(events).filter(Boolean) as CalendarEvent[]
  }

  if (typeof events === 'object') {
    const rawEvents = toRaw(events as Record<PropertyKey, unknown>)

    // 兼容 Ref<{ value: [] }> / Vue 响应式对象包装
    if (Array.isArray((rawEvents as { value?: unknown }).value)) {
      return ((rawEvents as { value: unknown[] }).value || []).filter(
        Boolean,
      ) as CalendarEvent[]
    }

    const iterator = (rawEvents as Record<PropertyKey, unknown>)[
      Symbol.iterator
    ]
    if (typeof iterator === 'function') {
      return Array.from(rawEvents as unknown as Iterable<unknown>).filter(
        Boolean,
      ) as CalendarEvent[]
    }

    return Object.values(rawEvents).filter(Boolean) as CalendarEvent[]
  }

  return []
}

// 跨日事件通用排序比较器（按跨度天数、时长、起始时间降/升序）。
const compareCrossDaySort = (left: CalendarEvent, right: CalendarEvent) => {
  const leftMetrics = getCrossDaySortMetrics(left)
  const rightMetrics = getCrossDaySortMetrics(right)
  if (rightMetrics.spanDays !== leftMetrics.spanDays) {
    return rightMetrics.spanDays - leftMetrics.spanDays
  }
  if (rightMetrics.durationMinutes !== leftMetrics.durationMinutes) {
    return rightMetrics.durationMinutes - leftMetrics.durationMinutes
  }
  return leftMetrics.startAt - rightMetrics.startAt
}

const sortMonthCellEvents = (left: CalendarEvent, right: CalendarEvent) => {
  const result = compareCrossDaySort(left, right)
  if (result !== 0) return result
  return String(left.title || '').localeCompare(String(right.title || ''))
}

const getSortedMonthCellEvents = (events: unknown) =>
  [...normalizeCellEvents(events)].sort(sortMonthCellEvents)

// 周/日视图顶部跨日事件最大显示行数，超出则显示「还有N条」

// 周/日视图顶部跨日事件是否处于展开状态
const headerEventsExpanded = ref(false)

// 月视图每行日程高度 = 20px height + 4px margin-bottom

// "还有N条" 按钮占用的高度估算（font-size:12px，无固定高度）


const getMonthCellVisibleLimit = (totalEvents: number) => {
  if (totalEvents <= 0) {
    return 0
  }

  // 若 dayMaxEvents 为数值，直接以数值为上限
  if (
    typeof props.dayMaxEvents === 'number' &&
    Number.isFinite(props.dayMaxEvents)
  ) {
    return Math.max(Math.floor(props.dayMaxEvents), 1)
  }

  // dayMaxEvents === false：不限制，全部显示
  if (props.dayMaxEvents === false) {
    return totalEvents
  }

  // dayMaxEvents === true（默认）：根据格子可用高度动态计算
  const bodyHeight = monthCellBodyHeight.value
  if (!bodyHeight) {
    // 高度尚未测量到，暂不裁切
    return totalEvents
  }

  // 总是预留"更多"按钮的高度，确保当有隐藏事件时不会layout抖动
  return Math.max(
    Math.floor((bodyHeight - MONTH_MORE_BTN_HEIGHT) / MONTH_EVENT_ROW_HEIGHT),
    0,
  )
}

const getMonthCellVisibleEvents = (cell: { events?: unknown }) => {
  const events = getSortedMonthCellEvents(cell?.events)
  const limit = getMonthCellVisibleLimit(events.length)
  if (limit >= events.length) {
    return events
  }
  // 若新增草稿事件超出可见范围，强制将其保留在末尾可见位置，确保 querySelector('.is-new') 能找到 DOM 锚点
  const newIdx = events.findIndex(
    (e) => getEventIdentifier(e) === newDraftEventIdRef.value,
  )
  if (newIdx === -1 || newIdx < limit) {
    return events.slice(0, limit)
  }
  return [...events.slice(0, limit - 1), events[newIdx]]
}

const getMonthCellHiddenEventCount = (cell: { events?: unknown }) => {
  const events = getSortedMonthCellEvents(cell?.events)
  return Math.max(events.length - getMonthCellVisibleLimit(events.length), 0)
}

// 将 vue-cal 5 view payload 归一化为组件内部 dates-change 原始结构。
const mapViewToRaw = (view: any): CalendarDatesChangeRaw => ({
  view: (view?.id ?? 'month') as 'month' | 'week' | 'day',
  startDate: view?.start ?? new Date(),
  endDate: view?.end ?? new Date(),
  firstCellDate: view?.extendedStart,
  lastCellDate: view?.extendedEnd,
  events: normalizeCalendarEvents(view?.events),
})

// 事件识别优先级：id > _eid > 兜底字段组合，保证编辑/拖拽时能定位到同一条记录。
const getEventIdentifier = (event?: Partial<CalendarEvent>) => {
  if (!event) {
    return ''
  }

  if (event.id !== undefined && event.id !== null) {
    return String(event.id)
  }

  if (event._eid) {
    return event._eid
  }

  return `fallback:${String(event.title || '')}|${String(normalizeDateLikeValue(event.start) || '')}|${String(normalizeDateLikeValue(event.end) || '')}`
}

const currentViewId = ref<'month' | 'week' | 'day'>(props.view)
const renderedViewId = ref<CalendarView>(props.view)

const internalCurrentDate = ref<Date>(
  toDateValue(props.currentDate) || new Date(),
)

// 用户最后一次明确选中/操作的日期（单元格点击、事件点击、日期选择器、今日按钮、视图内导航）。
// 切换视图模式时优先以此为锚点，无值时回退到 internalCurrentDate。
const selectedDate = ref<Date | null>(toDateValue(props.currentDate) || null)

const resolvedCurrentDate = computed(() => internalCurrentDate.value)

const headerPickerType = computed(() => {
  if (currentViewId.value === 'month') {
    return 'month'
  }

  if (currentViewId.value === 'week') {
    return 'week'
  }

  return 'date'
})

const headerPickerValue = computed(() => dayjs(internalCurrentDate.value))

const getWeekRangeByDate = (dateValue: Dayjs) => {
  const base = dateValue.startOf('day')
  const weekday = base.day()
  const offset = resolvedWeekStartDay.value === 1 ? (weekday + 6) % 7 : weekday
  const start = base.subtract(offset, 'day').startOf('day')
  const end = start.add(6, 'day').endOf('day')

  return { start, end }
}

const getWeekYearAndNumber = (weekStart: Dayjs) => {
  const resolveWeekYear = (targetWeekStart: Dayjs) => {
    const currentYear = targetWeekStart.year()
    const currentYearFirstWeekStart = getWeekRangeByDate(
      dayjs(`${currentYear}-01-01`),
    ).start
    const nextYearFirstWeekStart = getWeekRangeByDate(
      dayjs(`${currentYear + 1}-01-01`),
    ).start

    if (targetWeekStart.isBefore(currentYearFirstWeekStart)) {
      return currentYear - 1
    }

    if (!targetWeekStart.isBefore(nextYearFirstWeekStart)) {
      return currentYear + 1
    }

    return currentYear
  }

  const weekYear = resolveWeekYear(weekStart)
  const firstWeekStart = getWeekRangeByDate(dayjs(`${weekYear}-01-01`)).start
  const weekNo = Math.floor(weekStart.diff(firstWeekStart, 'day') / 7) + 1

  return {
    weekYear,
    weekNo: Math.max(weekNo, 1),
  }
}

const formatWeekHeaderTitle = (
  dateValue: Dayjs,
  format: string = props.weekTitleFormat,
) => {
  if (!dateValue || !dateValue.isValid()) {
    return ''
  }

  const weekRange = getWeekRangeByDate(dateValue)
  const weekInfo = getWeekYearAndNumber(weekRange.start)
  const start = weekRange.start
  const end = weekRange.end

  switch (format) {
    case 'YYYY 第W周':
      return `${weekInfo.weekYear} 第${weekInfo.weekNo}周`
    case 'YYYY年 第W周':
      return `${weekInfo.weekYear}年 第${weekInfo.weekNo}周`
    case '第W周':
      return `第${weekInfo.weekNo}周`
    case 'W周':
      return `${weekInfo.weekNo}周`
    case 'YYYY/W':
      return `${weekInfo.weekYear}/${weekInfo.weekNo}`
    case 'W/YYYY':
      return `${weekInfo.weekNo}/${weekInfo.weekYear}`
    case 'MM/DD - MM/DD':
      return `${start.format('MM/DD')} - ${end.format('MM/DD')}`
    case 'YYYY/MM/DD - MM/DD':
      return `${start.format('YYYY/MM/DD')} - ${end.format('MM/DD')}`
    case 'YYYY年M月D日-D日':
      return `${start.format('YYYY年M月D日')}-${end.format('D日')}`
    case 'YYYY年M月D日 - M月D日':
      return `${start.format('YYYY年M月D日')} - ${end.format('M月D日')}`
    case 'Week W, YYYY':
      return `Week ${weekInfo.weekNo}, ${weekInfo.weekYear}`
    case 'YYYY-MM-DD / DD':
      return `${start.format('YYYY-MM-DD')} / ${end.format('DD')}`
    default:
      return dateValue.format(format)
  }
}

const headerPickerFormat = computed(() => {
  if (currentViewId.value === 'day') {
    return props.dayTitleFormat
  }

  if (currentViewId.value === 'month') {
    return props.monthTitleFormat
  }

  if (currentViewId.value === 'week') {
    const currentWeekTitleFormat = props.weekTitleFormat
    return (dateValue: Dayjs) =>
      formatWeekHeaderTitle(dateValue, currentWeekTitleFormat)
  }

  return undefined
})

const resolvedWeekStartDay = computed(() => {
  const value = Number(props.weekStartDay)
  if (value === 0 || value === 1) {
    return value
  }

  return 0
})

const weekStartsOnSunday = computed(() => {
  if (currentViewId.value !== 'week' && currentViewId.value !== 'month') {
    return false
  }

  // vue-cal 原生仅支持周日/周一起始。
  return resolvedWeekStartDay.value === 0
})

const resolvedHeight = computed(() => {
  if (typeof props.height === 'number') {
    return `${props.height}px`
  }

  if (typeof props.height === 'string' && /^\d+$/.test(props.height)) {
    return `${props.height}px`
  }

  return props.height
})

const panelStyle = computed(() => {
  const style: Record<string, string> = {
    height: resolvedHeight.value,
    position: 'relative',
  }
  if (currentViewId.value === 'week' && weekHeadingsHeight.value > 0) {
    style['--ll-calendar-headings-height'] = `${weekHeadingsHeight.value}px`
    style['--ll-calendar-scrollbar-start-offset'] =
      `${weekHeadingsHeight.value}px`
  } else if (scrollbarStartOffset.value > 0) {
    style['--ll-calendar-scrollbar-start-offset'] =
      `${scrollbarStartOffset.value}px`
  }
  return style
})

const currentViewRange = ref<{ start: Date; end: Date }>({
  start: new Date(internalCurrentDate.value),
  end: new Date(internalCurrentDate.value),
})
const preventOutOfMonthJumpPending = ref(false)
const monthAnchorBeforeOutOfMonthClick = ref<Date | null>(null)
const timeCellWidth = ref<number>()
const weekHeadingsHeight = ref(0)
const scrollbarStartOffset = ref(0)
const monthCellBodyHeight = ref(0)
let weekHeadingsObserver: ResizeObserver | null = null
let weekHeadingsMutationObserver: MutationObserver | null = null
let observedWeekHeadingsEl: HTMLElement | null = null

const timeFromMinutes = computed(() => parseTimeToMinutes(props.slotMinTime))

const timeToMinutes = computed(() => parseTimeToMinutes(props.slotMaxTime))

const slotStepMinutes = computed(() =>
  Math.max(parseTimeToMinutes(props.slotDuration), 15),
)

const isWeekOrDayView = computed(
  () => currentViewId.value === 'week' || currentViewId.value === 'day',
)

// 返回当前视图对应可滚动容器的 CSS 选择器。
const getViewScrollableSelector = () =>
  currentViewId.value === 'week'
    ? '.vuecal__scrollable--week-view'
    : '.vuecal__scrollable--day-view'

// 事件列表弹出面板定位计算。





const calcEventListPanelPosition = (nativeEvent: MouseEvent) => {
  const vw = window.innerWidth
  const vh = window.innerHeight
  let left = nativeEvent.clientX + EVENT_LIST_PANEL_GAP
  let top = nativeEvent.clientY - EVENT_LIST_PANEL_HEIGHT / 2
  if (left + EVENT_LIST_PANEL_WIDTH + EVENT_LIST_PANEL_MARGIN > vw) {
    left = nativeEvent.clientX - EVENT_LIST_PANEL_WIDTH - EVENT_LIST_PANEL_GAP
  }
  left = Math.max(
    EVENT_LIST_PANEL_MARGIN,
    Math.min(left, vw - EVENT_LIST_PANEL_WIDTH - EVENT_LIST_PANEL_MARGIN),
  )
  top = Math.max(
    EVENT_LIST_PANEL_MARGIN,
    Math.min(top, vh - EVENT_LIST_PANEL_HEIGHT - EVENT_LIST_PANEL_MARGIN),
  )
  return { position: 'fixed', left: `${left}px`, top: `${top}px` }
}

const calendarEventsForView = computed(() => {
  if (!isWeekOrDayView.value) {
    // 返回浅拷贝，隔离 vue-cal 对传入 events 数组的原地 splice，避免清空内部事件源数组
    return [...internalEvents.value] as any[]
  }

  if (currentViewId.value === 'day') {
    const currentDate = toDateValue(internalCurrentDate.value) || new Date()
    const dayStart = dayjs(currentDate).startOf('day').toDate()
    const dayEnd = dayjs(currentDate).endOf('day').toDate()

    return internalEvents.value.filter(
      (event) =>
        isEventOverlappingRange(event, dayStart, dayEnd) &&
        !isCrossDayEvent(event),
    ) as any[]
  }

  return internalEvents.value.filter(
    (event) => !isCrossDayEvent(event),
  ) as any[]
})

const crossDayReferenceEvents = computed(() => {
  if (!isWeekOrDayView.value) {
    return [] as CalendarEvent[]
  }

  const { start, end } = currentViewRange.value
  const events = internalEvents.value.filter(
    (event) =>
      isCrossDayEvent(event) && isEventOverlappingRange(event, start, end),
  )

  return [...events].sort(compareCrossDaySort)
})

const weekdayIdToLabel = (id: string) => {
  const labelMaps: Record<string, Record<string, string>> = {
    min: {
      mon: '一',
      tue: '二',
      wed: '三',
      thu: '四',
      fri: '五',
      sat: '六',
      sun: '日',
    },
    short: {
      mon: '周一',
      tue: '周二',
      wed: '周三',
      thu: '周四',
      fri: '周五',
      sat: '周六',
      sun: '周日',
    },
    long: {
      mon: '星期一',
      tue: '星期二',
      wed: '星期三',
      thu: '星期四',
      fri: '星期五',
      sat: '星期六',
      sun: '星期日',
    },
  }

  const weekdayId = String(id).toLowerCase()
  const currentFormatMap = labelMaps[props.weekdayFormat] || labelMaps.long
  return currentFormatMap[weekdayId] ?? id
}

const formatWeekdayDate = (dateStr: string) => {
  const target = dayjs(dateStr)
  if (!target.isValid()) {
    return ''
  }

  if (currentViewId.value !== 'week') {
    if (target.date() === 1) {
      return formatFirstDayLabel(target)
    }

    return target.format('D')
  }

  if (target.date() === 1) {
    return formatFirstDayLabel(target)
  }

  return target.format(props.weekColumnDateFormat)
}

const formatMonthCellDate = (dateValue: string | Date) => {
  const target = dayjs(dateValue)
  if (!target.isValid()) {
    return ''
  }

  if (target.date() === 1) {
    return formatFirstDayLabel(target)
  }

  return target.format('D')
}

const formatFirstDayLabel = (target: Dayjs) => {
  const month = target.month() + 1
  const day = target.date()

  switch (props.firstDayFormat) {
    case 'monthDay':
      return `${month}/${day}`
    case 'dayMonth':
      return `${day}/${month}`
    case 'chineseDate':
      return `${month}月${day}日`
    default:
      return target.format(props.firstDayFormat)
  }
}

const selectedDayPoint = computed(() =>
  dayjs(toDateValue(internalCurrentDate.value) || new Date()).startOf('day'),
)

const dayHeaderTimezoneLabel = computed(() => {
  const offsetMinutes = -new Date().getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = Math.floor(absoluteMinutes / 60)
  const minutes = absoluteMinutes % 60

  if (minutes === 0) {
    return `GMT${sign}${hours}`
  }

  return `GMT${sign}${hours}:${String(minutes).padStart(2, '0')}`
})

const weekHeaderTimezoneStyle = computed(() => {
  if (!weekHeadingsHeight.value) {
    return undefined
  }

  return {
    top: scrollbarStartOffset.value + 'px',
    height: weekHeadingsHeight.value - 10 + 'px',
  }
})

const dayHeaderTimezoneStyle = computed(() => {
  if (!timeCellWidth.value) {
    return undefined
  }

  const width = `${timeCellWidth.value}px`
  return {
    width,
    minWidth: width,
  }
})

const syncTimeCellWidth = () => {
  const hostEl = calendarContainerRef.value
  if (!hostEl) {
    return
  }

  const timeCellEl = hostEl.querySelector(
    '.vuecal__time-column .vuecal__time-cell',
  )
  if (!(timeCellEl instanceof HTMLElement)) {
    return
  }

  const nextWidth = Math.round(timeCellEl.getBoundingClientRect().width)
  if (nextWidth > 0) {
    timeCellWidth.value = nextWidth
  }
}

const scheduleTimeCellWidthSync = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.requestAnimationFrame(() => {
    syncTimeCellWidth()
  })
}

const cleanupWeekHeadingsObservers = () => {
  weekHeadingsObserver?.disconnect()
  weekHeadingsObserver = null
  weekHeadingsMutationObserver?.disconnect()
  weekHeadingsMutationObserver = null
  observedWeekHeadingsEl = null
}

// ---- 月视图格子高度 Observer ----

let monthCellObserver: ResizeObserver | null = null

const measureMonthCellHeight = () => {
  const hostEl = calendarContainerRef.value
  if (!hostEl) {
    return
  }

  const cellEl = hostEl.querySelector<HTMLElement>(
    '.vuecal__scrollable--month-view .vuecal__cell',
  )
  if (!cellEl) {
    return
  }

  const cellHeight = cellEl.clientHeight
  if (cellHeight <= 0) {
    return
  }

  const dateEl = cellEl.querySelector<HTMLElement>('.vuecal__cell-date')
  const dateHeight = dateEl ? dateEl.offsetHeight : 24
  const bodyHeight = Math.max(cellHeight - dateHeight, 0)
  if (bodyHeight > 0) {
    monthCellBodyHeight.value = bodyHeight
  }
}

const cleanupMonthCellObserver = () => {
  monthCellObserver?.disconnect()
  monthCellObserver = null
}

const bindMonthCellObserver = () => {
  cleanupMonthCellObserver()

  if (typeof ResizeObserver === 'undefined') {
    return
  }

  const hostEl = calendarContainerRef.value
  if (!hostEl) {
    return
  }

  const cellEl = hostEl.querySelector<HTMLElement>(
    '.vuecal__scrollable--month-view .vuecal__cell',
  )
  if (!cellEl) {
    return
  }

  measureMonthCellHeight()
  monthCellObserver = new ResizeObserver(() => {
    measureMonthCellHeight()
  })
  monthCellObserver.observe(cellEl)
}

const syncMonthCellHeight = () => {
  if (currentViewId.value !== 'month') {
    cleanupMonthCellObserver()
    return
  }

  // 双 rAF 确保 VueCal 已完成 DOM 渲染
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (currentViewId.value !== 'month') {
        cleanupMonthCellObserver()
        return
      }

      bindMonthCellObserver()
    })
  })
}

const measureWeekHeadingsHeight = (headingsEl: HTMLElement) => {
  const nextHeight = Math.round(headingsEl.getBoundingClientRect().height)
  if (nextHeight > 0) {
    weekHeadingsHeight.value = nextHeight
  }
}

const bindWeekHeadingsElement = (hostEl: HTMLElement) => {
  const headingsEl = hostEl.querySelector('.vuecal__headings')
  if (!(headingsEl instanceof HTMLElement)) {
    return
  }

  if (observedWeekHeadingsEl === headingsEl) {
    measureWeekHeadingsHeight(headingsEl)
    return
  }

  weekHeadingsObserver?.disconnect()
  observedWeekHeadingsEl = headingsEl
  measureWeekHeadingsHeight(headingsEl)

  weekHeadingsObserver = new ResizeObserver(() => {
    if (observedWeekHeadingsEl) {
      measureWeekHeadingsHeight(observedWeekHeadingsEl)
    }
  })
  weekHeadingsObserver.observe(headingsEl)
}

const syncWeekHeadingsHeight = () => {
  cleanupWeekHeadingsObservers()

  if (currentViewId.value !== 'week') {
    return
  }

  if (
    typeof ResizeObserver === 'undefined' ||
    typeof MutationObserver === 'undefined'
  ) {
    return
  }

  const hostEl = calendarContainerRef.value
  if (!hostEl) {
    return
  }

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      if (currentViewId.value !== 'week') {
        return
      }

      bindWeekHeadingsElement(hostEl)

      weekHeadingsMutationObserver = new MutationObserver(() => {
        if (currentViewId.value !== 'week') {
          return
        }

        bindWeekHeadingsElement(hostEl)
      })
      weekHeadingsMutationObserver.observe(hostEl, {
        childList: true,
        subtree: true,
      })
    })
  })
}

const scheduleWeekHeadingsHeightSync = () => {
  syncWeekHeadingsHeight()
}

const shouldShowCustomNowLine = computed(
  () =>
    props.nowIndicator &&
    (currentViewId.value === 'week' || currentViewId.value === 'day'),
)

const isTransientScrollbarVisible = ref(false)
const customScrollbarTrackRef = ref<HTMLElement>()
const customScrollbarThumbTopPx = ref(0)
const customScrollbarThumbHeightPx = ref(0)

const customScrollbarVisible = computed(
  () => currentViewId.value === 'week' || currentViewId.value === 'day',
)

const customScrollbarTrackStyle = computed(() => {
  // scrollbarStartOffset = vuecal__header 高度（scrollable 容器相对组件顶部的偏移）
  // weekHeadingsHeight = sticky 表头（日期列）的高度
  // 两者之和 = 时间轴起始位置
  const headingsHeight =
    currentViewId.value === 'week' && weekHeadingsHeight.value > 0
      ? weekHeadingsHeight.value
      : 0
  const top = scrollbarStartOffset.value + headingsHeight
  return {
    top: `${top}px`,
    bottom: '4px',
    right: '6px',
  }
})

const customScrollbarThumbStyle = computed(() => ({
  top: `${customScrollbarThumbTopPx.value}px`,
  height: `${customScrollbarThumbHeightPx.value}px`,
}))

const updateCustomScrollbarThumb = () => {
  const el = transientScrollbarScrollableEl
  const trackEl = customScrollbarTrackRef.value
  if (!el || !trackEl) return
  const { scrollTop, scrollHeight, clientHeight } = el
  const trackH = trackEl.clientHeight
  if (scrollHeight <= clientHeight || trackH <= 0) {
    customScrollbarThumbHeightPx.value = trackH
    customScrollbarThumbTopPx.value = 0
    return
  }
  const thumbH = Math.max((clientHeight / scrollHeight) * trackH, 24)
  const thumbT = (scrollTop / (scrollHeight - clientHeight)) * (trackH - thumbH)
  customScrollbarThumbHeightPx.value = thumbH
  customScrollbarThumbTopPx.value = thumbT
}

const handleScrollbarThumbMousedown = (e: MouseEvent) => {
  e.preventDefault()
  const el = transientScrollbarScrollableEl
  const trackEl = customScrollbarTrackRef.value
  if (!el || !trackEl) return
  const startY = e.clientY
  const startScrollTop = el.scrollTop
  const { scrollHeight, clientHeight } = el
  const trackH = trackEl.clientHeight
  const thumbH = customScrollbarThumbHeightPx.value
  const onMousemove = (moveEvent: MouseEvent) => {
    const dy = moveEvent.clientY - startY
    const scrollRange = scrollHeight - clientHeight
    const thumbRange = trackH - thumbH
    if (thumbRange <= 0) return
    el.scrollTop = startScrollTop + (dy / thumbRange) * scrollRange
    showTransientScrollbar()
  }
  const onMouseup = () => {
    document.removeEventListener('mousemove', onMousemove)
    document.removeEventListener('mouseup', onMouseup)
  }
  document.addEventListener('mousemove', onMousemove)
  document.addEventListener('mouseup', onMouseup)
}

const handleScrollbarTrackMousedown = (e: MouseEvent) => {
  const el = transientScrollbarScrollableEl
  const trackEl = customScrollbarTrackRef.value
  if (!el || !trackEl) return
  const trackRect = trackEl.getBoundingClientRect()
  const clickY = e.clientY - trackRect.top
  const { scrollHeight, clientHeight } = el
  const trackH = trackEl.clientHeight
  const thumbH = customScrollbarThumbHeightPx.value
  const thumbRange = trackH - thumbH
  const scrollRange = scrollHeight - clientHeight
  if (thumbRange <= 0) return
  el.scrollTop = ((clickY - thumbH / 2) / thumbRange) * scrollRange
  showTransientScrollbar()
}

let customNowLineAlignTimeoutId = 0
let customNowLineIntervalId = 0
let customNowLineScrollableEl: HTMLElement | null = null
let customNowLineHighlightedScaleCellEl: HTMLElement | null = null
let customNowLineHighlightedScaleCellColor = ''
let transientScrollbarTimeoutId = 0
let transientScrollbarScrollableEl: HTMLElement | null = null

const clearCustomNowLineScaleHighlight = () => {
  if (customNowLineHighlightedScaleCellEl) {
    if (customNowLineHighlightedScaleCellColor) {
      customNowLineHighlightedScaleCellEl.style.color =
        customNowLineHighlightedScaleCellColor
    } else {
      customNowLineHighlightedScaleCellEl.style.removeProperty('color')
    }
  }

  customNowLineHighlightedScaleCellEl = null
  customNowLineHighlightedScaleCellColor = ''
  customNowLineTimeHidden.value = false
}

const clearCustomNowLineTimer = () => {
  if (customNowLineAlignTimeoutId) {
    window.clearTimeout(customNowLineAlignTimeoutId)
    customNowLineAlignTimeoutId = 0
  }

  if (customNowLineIntervalId) {
    window.clearInterval(customNowLineIntervalId)
    customNowLineIntervalId = 0
  }
}

const clearTransientScrollbarTimer = () => {
  if (transientScrollbarTimeoutId) {
    window.clearTimeout(transientScrollbarTimeoutId)
    transientScrollbarTimeoutId = 0
  }
}

const syncScrollbarStartOffset = () => {
  const hostEl = calendarContainerRef.value
  if (!hostEl) {
    scrollbarStartOffset.value = 0
    return
  }

  if (currentViewId.value !== 'week' && currentViewId.value !== 'day') {
    scrollbarStartOffset.value = 0
    return
  }

  const containerRect = hostEl.getBoundingClientRect()
  const scrollableEl = hostEl.querySelector(getViewScrollableSelector())

  let nextOffset = 0
  if (scrollableEl instanceof HTMLElement) {
    const scrollableRect = scrollableEl.getBoundingClientRect()
    nextOffset = Math.max(Math.round(scrollableRect.top - containerRect.top), 0)
  }

  if (scrollbarStartOffset.value !== nextOffset) {
    scrollbarStartOffset.value = nextOffset
  }
}

const showTransientScrollbar = () => {
  isTransientScrollbarVisible.value = true
  clearTransientScrollbarTimer()
  transientScrollbarTimeoutId = window.setTimeout(() => {
    isTransientScrollbarVisible.value = false
    transientScrollbarTimeoutId = 0
  }, 900)
}

const handleTransientScrollbarScroll = () => {
  showTransientScrollbar()
  updateCustomScrollbarThumb()
}

const bindTransientScrollbarListener = () => {
  const hostEl = calendarContainerRef.value
  if (!hostEl) {
    return
  }

  if (currentViewId.value !== 'week' && currentViewId.value !== 'day') {
    if (transientScrollbarScrollableEl) {
      transientScrollbarScrollableEl.removeEventListener(
        'scroll',
        handleTransientScrollbarScroll,
      )
      transientScrollbarScrollableEl = null
    }
    isTransientScrollbarVisible.value = false
    clearTransientScrollbarTimer()
    return
  }

  const scrollableEl = hostEl.querySelector(getViewScrollableSelector())

  if (!(scrollableEl instanceof HTMLElement)) {
    if (transientScrollbarScrollableEl) {
      transientScrollbarScrollableEl.removeEventListener(
        'scroll',
        handleTransientScrollbarScroll,
      )
      transientScrollbarScrollableEl = null
    }
    return
  }

  if (transientScrollbarScrollableEl === scrollableEl) {
    return
  }

  if (transientScrollbarScrollableEl) {
    transientScrollbarScrollableEl.removeEventListener(
      'scroll',
      handleTransientScrollbarScroll,
    )
  }

  transientScrollbarScrollableEl = scrollableEl
  transientScrollbarScrollableEl.addEventListener(
    'scroll',
    handleTransientScrollbarScroll,
  )
  updateCustomScrollbarThumb()
}

const scheduleTransientScrollbarListenerSync = () => {
  if (typeof window === 'undefined') {
    return
  }

  const runSync = () => {
    syncScrollbarStartOffset()
    bindTransientScrollbarListener()
    updateCustomScrollbarThumb()
  }

  nextTick(() => {
    runSync()
    window.requestAnimationFrame(() => {
      runSync()
      window.requestAnimationFrame(runSync)
    })
    window.setTimeout(runSync, 80)
  })
}

const bindCustomNowLineScrollListener = () => {
  const hostEl = calendarContainerRef.value
  if (!hostEl) {
    return
  }

  const scrollableEl = hostEl.querySelector(getViewScrollableSelector())

  if (!(scrollableEl instanceof HTMLElement)) {
    if (customNowLineScrollableEl) {
      customNowLineScrollableEl.removeEventListener(
        'scroll',
        updateCustomNowLinePosition,
      )
      customNowLineScrollableEl = null
    }
    return
  }

  if (customNowLineScrollableEl === scrollableEl) {
    return
  }

  if (customNowLineScrollableEl) {
    customNowLineScrollableEl.removeEventListener(
      'scroll',
      updateCustomNowLinePosition,
    )
  }

  customNowLineScrollableEl = scrollableEl
  customNowLineScrollableEl.addEventListener(
    'scroll',
    updateCustomNowLinePosition,
  )
}

function updateCustomNowLinePosition() {
  clearCustomNowLineScaleHighlight()

  if (!shouldShowCustomNowLine.value) {
    customNowLineVisible.value = false
    return
  }

  const hostEl = calendarContainerRef.value
  if (!hostEl) {
    customNowLineVisible.value = false
    return
  }

  const bodyEl = hostEl.querySelector('.vuecal__body')
  if (!(bodyEl instanceof HTMLElement)) {
    customNowLineVisible.value = false
    return
  }

  const scrollableEl =
    customNowLineScrollableEl ||
    hostEl.querySelector(getViewScrollableSelector())
  if (!(scrollableEl instanceof HTMLElement)) {
    customNowLineVisible.value = false
    return
  }

  const now = new Date()
  const minutesNow =
    now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60
  const from = timeFromMinutes.value
  const to = timeToMinutes.value

  if (minutesNow < from || minutesNow > to || slotStepMinutes.value <= 0) {
    customNowLineVisible.value = false
    return
  }

  const containerRect = hostEl.getBoundingClientRect()
  const bodyRect = bodyEl.getBoundingClientRect()
  const rawLineTop =
    bodyRect.top -
    containerRect.top +
    ((minutesNow - from) / slotStepMinutes.value) * props.timeCellHeight
  const lineTop = rawLineTop

  const scrollableRect = scrollableEl.getBoundingClientRect()
  const visibleTop = scrollableRect.top - containerRect.top
  const visibleBottom = scrollableRect.bottom - containerRect.top

  // 计算时间线相对于 scrollable 可见顶部的距离
  const lineTopRelativeToScrollable = lineTop - visibleTop

  if (lineTop < visibleTop || lineTop > visibleBottom) {
    customNowLineVisible.value = false
    return
  }

  // 当时间线滚动到头部附近时隐藏（距离 sticky 表头底部太近）
  const headHideThreshold = weekHeadingsHeight.value + 3
  if (lineTopRelativeToScrollable < headHideThreshold) {
    customNowLineVisible.value = false
    return
  }

  let dotLeft = -5
  if (currentViewId.value === 'week') {
    const todayCellEl = hostEl.querySelector(
      '.vuecal__scrollable--week-view .vuecal__body .vuecal__cell--today',
    )

    if (todayCellEl instanceof HTMLElement) {
      const todayRect = todayCellEl.getBoundingClientRect()
      dotLeft = Math.round(todayRect.left - bodyRect.left - 4)
    }
  }

  customNowLineStyle.value = {
    left: `${Math.round(bodyRect.left - containerRect.left)}px`,
    top: `${Math.round(lineTop)}px`,
    width: `${Math.round(bodyRect.width)}px`,
    '--ll-custom-now-dot-left': `${dotLeft}px`,
  }

  const lineTopInViewport = containerRect.top + lineTop
  const timeCells = Array.from(
    hostEl.querySelectorAll('.vuecal__time-column .vuecal__time-cell'),
  ).filter((node): node is HTMLElement => node instanceof HTMLElement)

  let nearestCell: HTMLElement | null = null
  let nearestDelta = Number.POSITIVE_INFINITY
  for (const cell of timeCells) {
    const delta = Math.abs(cell.getBoundingClientRect().top - lineTopInViewport)
    if (delta < nearestDelta) {
      nearestDelta = delta
      nearestCell = cell
    }
  }

  // 与时间刻度线重合时，隐藏时间线时间并高亮刻度时间。
  // 使用较宽容差，避免缩放或字体渲染导致看起来重合却像素不完全一致。
  if (nearestCell && nearestDelta <= 6) {
    customNowLineHighlightedScaleCellEl = nearestCell
    customNowLineHighlightedScaleCellColor = nearestCell.style.color
    nearestCell.style.color = '#ff4d4f'
    customNowLineTimeHidden.value = true
  }

  customNowLineTime.value = dayjs(now).format(props.timeSlotFormat || 'HH:mm')
  customNowLineVisible.value = true
}

const scheduleCustomNowLinePositionSync = (hideFirst = false) => {
  if (typeof window === 'undefined') {
    return
  }

  if (hideFirst) {
    customNowLineVisible.value = false
  }

  const runSync = () => {
    bindCustomNowLineScrollListener()
    updateCustomNowLinePosition()
  }

  const retryCount = hideFirst ? 3 : 2
  let attempt = 0

  const attemptSync = () => {
    attempt++
    runSync()

    // 如果还未显示且未达到最大重试次数，继续重试
    if (!customNowLineVisible.value && attempt < retryCount) {
      const delay = hideFirst
        ? attempt === 1
          ? 0
          : attempt === 2
            ? 100
            : 200
        : 0
      if (delay > 0) {
        window.setTimeout(
          () => window.requestAnimationFrame(attemptSync),
          delay,
        )
      } else {
        window.requestAnimationFrame(attemptSync)
      }
    }
  }

  nextTick(() => {
    window.requestAnimationFrame(attemptSync)
  })
}

const startCustomNowLineTicker = () => {
  clearCustomNowLineTimer()

  if (!shouldShowCustomNowLine.value) {
    customNowLineVisible.value = false
    return
  }

  scheduleCustomNowLinePositionSync()

  const now = new Date()
  const delay = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

  customNowLineAlignTimeoutId = window.setTimeout(
    () => {
      scheduleCustomNowLinePositionSync()
      customNowLineIntervalId = window.setInterval(() => {
        scheduleCustomNowLinePositionSync()
      }, 60 * 1000)
    },
    Math.max(delay, 0),
  )
}

const stopCustomNowLineTicker = () => {
  clearCustomNowLineTimer()
  clearCustomNowLineScaleHighlight()
  if (customNowLineScrollableEl) {
    customNowLineScrollableEl.removeEventListener(
      'scroll',
      updateCustomNowLinePosition,
    )
    customNowLineScrollableEl = null
  }
  customNowLineVisible.value = false
}

const dayHeaderEvents = computed(() => {
  if (currentViewId.value !== 'day') {
    return [] as CalendarEvent[]
  }

  const dayStart = selectedDayPoint.value.toDate()
  const dayEnd = selectedDayPoint.value.endOf('day').toDate()
  const events = internalEvents.value.filter(
    (event) =>
      isCrossDayEvent(event) &&
      isEventOverlappingRange(event, dayStart, dayEnd),
  )

  return [...events].sort(compareCrossDaySort)
})

// 为当前视图范围内的跨日事件计算不重叠的行号，保证各列同一事件显示在同一行。
const weekCrossDayLayout = computed(() => {
  if (!isWeekOrDayView.value) {
    return { rowMap: new Map<string, number>(), totalRows: 0 }
  }

  const events = [...crossDayReferenceEvents.value].sort((left, right) => {
    const leftStart = dayjs(toDateValue(left.start) || 0)
      .startOf('day')
      .valueOf()
    const rightStart = dayjs(toDateValue(right.start) || 0)
      .startOf('day')
      .valueOf()
    if (leftStart !== rightStart) {
      return leftStart - rightStart
    }

    const leftEnd = dayjs(toDateValue(left.end) || toDateValue(left.start) || 0)
      .startOf('day')
      .valueOf()
    const rightEnd = dayjs(
      toDateValue(right.end) || toDateValue(right.start) || 0,
    )
      .startOf('day')
      .valueOf()
    if (leftEnd !== rightEnd) {
      // 相同开始日时，较长事件优先占位，减少后续覆盖抖动。
      return rightEnd - leftEnd
    }

    return getEventIdentifier(left).localeCompare(getEventIdentifier(right))
  })
  const rowMap = new Map<string, number>()
  // 每行已占用的最晚结束日（startOf('day') 时间戳）
  const rowLastEndDay: number[] = []

  for (const event of events) {
    const start = toDateValue(event.start)
    const end = toDateValue(event.end) || start
    if (!start || !end) continue

    const effectiveEnd = new Date(Math.max(end.getTime() - 1, start.getTime()))
    const eventStartDay = dayjs(start).startOf('day').valueOf()
    const eventEndDay = dayjs(effectiveEnd).startOf('day').valueOf()
    const key = getEventIdentifier(event)

    const row = rowLastEndDay.findIndex((lastEnd) => lastEnd < eventStartDay)
    const assignedRow = row === -1 ? rowLastEndDay.length : row
    rowLastEndDay[assignedRow] = eventEndDay
    rowMap.set(key, assignedRow)
  }

  return { rowMap, totalRows: rowLastEndDay.length }
})

// 任意列是否存在超出限制的跨日事件（用于控制展开按钮的显示）
const weekCrossDayHasMore = computed(
  () => weekCrossDayLayout.value.totalRows > MAX_HEADER_ROWS,
)

type CrossDaySlot = {
  event: CalendarEvent
  segment: 'start' | 'middle' | 'end' | 'single'
} | null

const getWeekHeaderEventSegment = (
  crossSlot: Exclude<CrossDaySlot, null>,
  dateStr: string,
): 'start' | 'middle' | 'end' | 'single' => {
  const colDay = dayjs(new Date(dateStr)).startOf('day')
  const weekFirstDay = dayjs(currentViewRange.value.start).startOf('day')

  if (!colDay.isSame(weekFirstDay)) {
    return crossSlot.segment
  }

  const start = toDateValue(crossSlot.event.start)
  const end = toDateValue(crossSlot.event.end) || start
  if (!start || !end) {
    return crossSlot.segment
  }

  const effectiveEnd = new Date(Math.max(end.getTime() - 1, start.getTime()))
  const eventStartDay = dayjs(start).startOf('day')
  const eventEndDay = dayjs(effectiveEnd).startOf('day')
  const isCrossDay = eventEndDay.isAfter(eventStartDay)

  if (!isCrossDay) {
    return crossSlot.segment
  }

  return colDay.isSame(eventEndDay) ? 'single' : 'start'
}

// 为指定日期列返回每行的跨日事件片段（null 表示该行在本列无事件）。
const getColumnCrossDayEvents = (dateStr: string): CrossDaySlot[] => {
  const { rowMap, totalRows } = weekCrossDayLayout.value
  if (totalRows === 0) return []

  const colDay = dayjs(new Date(dateStr)).startOf('day')
  const slots: CrossDaySlot[] = Array(totalRows).fill(null)

  for (const event of crossDayReferenceEvents.value) {
    const start = toDateValue(event.start)
    const end = toDateValue(event.end) || start
    if (!start || !end) continue

    const effectiveEnd = new Date(Math.max(end.getTime() - 1, start.getTime()))
    const eventStartDay = dayjs(start).startOf('day')
    const eventEndDay = dayjs(effectiveEnd).startOf('day')

    if (colDay.isBefore(eventStartDay) || colDay.isAfter(eventEndDay)) continue

    const key = getEventIdentifier(event)
    const row = rowMap.get(key)
    if (row === undefined) continue

    let segment: 'start' | 'middle' | 'end' | 'single'
    if (colDay.isSame(eventStartDay) && colDay.isSame(eventEndDay)) {
      segment = 'single'
    } else if (colDay.isSame(eventStartDay)) segment = 'start'
    else if (colDay.isSame(eventEndDay)) segment = 'end'
    else segment = 'middle'

    slots[row] = { event, segment }
  }

  return slots
}

const getColumnCrossDayVisibleEvents = (dateStr: string): CrossDaySlot[] =>
  headerEventsExpanded.value
    ? getColumnCrossDayEvents(dateStr)
    : getColumnCrossDayEvents(dateStr).slice(0, MAX_HEADER_ROWS)

const getColumnCrossDayHiddenCount = (dateStr: string): number =>
  headerEventsExpanded.value
    ? 0
    : getColumnCrossDayEvents(dateStr).slice(MAX_HEADER_ROWS).filter(Boolean)
        .length

const getColumnCrossDayAllEvents = (dateStr: string): CalendarEvent[] =>
  getColumnCrossDayEvents(dateStr)
    .filter((slot): slot is Exclude<CrossDaySlot, null> => slot !== null)
    .map((slot) => slot.event)

type WeekCrossDayDragState = {
  event: CalendarEvent
  sourceDay: string
}

const weekCrossDayDragState = ref<WeekCrossDayDragState | null>(null)
const weekCrossDayDropDay = ref('')
let suppressWeekCrossDayClickUntil = 0

const formatCrossDayColumnKey = (dateValue: string | Date) => {
  const target = dayjs(dateValue).startOf('day')
  return target.isValid() ? target.format('YYYY-MM-DD') : ''
}

const canDragWeekCrossDayEvent = (event?: CalendarEvent | null) =>
  Boolean(
    event &&
    currentViewId.value === 'week' &&
    !props.readonly &&
    currentEditableEvent.value.drag !== false,
  )

const isWeekCrossDayDragging = (event?: CalendarEvent | null) => {
  if (!event || !weekCrossDayDragState.value) {
    return false
  }

  return (
    getEventIdentifier(event) ===
    getEventIdentifier(weekCrossDayDragState.value.event)
  )
}

const isWeekCrossDayDropTarget = (dateValue: string | Date) => {
  const dayKey = formatCrossDayColumnKey(dateValue)
  return Boolean(dayKey && dayKey === weekCrossDayDropDay.value)
}

const shouldSuppressWeekCrossDayClick = () =>
  suppressWeekCrossDayClickUntil > Date.now()

const clearWeekCrossDayDragState = () => {
  weekCrossDayDragState.value = null
  weekCrossDayDropDay.value = ''
}

const handleWeekCrossDayDragStart = (
  event: CalendarEvent,
  sourceDate: string,
  dragEvent: DragEvent,
) => {
  if (!canDragWeekCrossDayEvent(event)) {
    dragEvent.preventDefault()
    return
  }

  const sourceDay = formatCrossDayColumnKey(sourceDate)
  if (!sourceDay) {
    dragEvent.preventDefault()
    return
  }

  weekCrossDayDragState.value = {
    event: normalizeCalendarEvent(event),
    sourceDay,
  }
  weekCrossDayDropDay.value = sourceDay

  if (dragEvent.dataTransfer) {
    dragEvent.dataTransfer.effectAllowed = 'move'
    dragEvent.dataTransfer.dropEffect = 'move'
    dragEvent.dataTransfer.setData('text/plain', getEventIdentifier(event))
  }
}

const handleWeekCrossDayDragOver = (
  dateValue: string,
  dragEvent: DragEvent,
) => {
  if (!weekCrossDayDragState.value) {
    return
  }

  const targetDay = formatCrossDayColumnKey(dateValue)
  if (!targetDay) {
    return
  }

  dragEvent.preventDefault()
  weekCrossDayDropDay.value = targetDay

  if (dragEvent.dataTransfer) {
    dragEvent.dataTransfer.dropEffect = 'move'
  }
}

const moveWeekCrossDayEvent = (
  targetDay: string,
  dragState: WeekCrossDayDragState,
) => {
  const start = toDateValue(dragState.event.start)
  const end = toDateValue(dragState.event.end) || start
  if (!start || !end) {
    clearWeekCrossDayDragState()
    return
  }

  const sourceDay = dayjs(dragState.sourceDay)
  const nextTargetDay = dayjs(targetDay)
  if (!sourceDay.isValid() || !nextTargetDay.isValid()) {
    clearWeekCrossDayDragState()
    return
  }

  const diffDays = nextTargetDay.diff(sourceDay, 'day')
  if (diffDays === 0) {
    clearWeekCrossDayDragState()
    return
  }

  const newStart = dayjs(start).add(diffDays, 'day').toDate()

  // 禁止拖拽到过去日期
  if (props.disableCreateInPast && newStart.getTime() < Date.now()) {
    clearWeekCrossDayDragState()
    return
  }

  const normalizedEvent = normalizeCalendarEvent({
    ...dragState.event,
    start: newStart,
    end: dayjs(end).add(diffDays, 'day').toDate(),
  })
  const originalEvent = normalizeCalendarEvent(dragState.event)
  const nextEvents = patchEvent(normalizedEvent, originalEvent)

  scheduleInteractionCommit(() => {
    commitEvents(nextEvents)

    emit('event-drop', {
      raw: {
        event: normalizedEvent,
        originalEvent,
        external: false,
      },
      event: normalizedEvent,
      events: normalizeCalendarEvents(nextEvents),
    })
  })

  suppressWeekCrossDayClickUntil = Date.now() + 250
  clearWeekCrossDayDragState()
}

const handleWeekCrossDayDrop = (dateValue: string, dragEvent: DragEvent) => {
  if (!weekCrossDayDragState.value) {
    return
  }

  dragEvent.preventDefault()
  const targetDay = formatCrossDayColumnKey(dateValue)
  if (!targetDay) {
    clearWeekCrossDayDragState()
    return
  }

  moveWeekCrossDayEvent(targetDay, weekCrossDayDragState.value)
}

const handleWeekCrossDayDragEnd = () => {
  clearWeekCrossDayDragState()
}

const resolvedEventDisplayFields = computed(() => {
  if (props.eventDisplayFields.length) {
    return props.eventDisplayFields
  }

  if (props.eventMetaFields.length) {
    return props.eventMetaFields.slice(0, props.eventMaxMetaCount)
  }

  return []
})

const mergedFieldComponents = computed(() => ({
  ...defaultCalendarFieldComponents,
  ...props.components,
}))

const isRowDisplayField = (field: CalendarEventDisplayField) =>
  field.type === 'row'

const isComponentDisplayField = (field: CalendarEventDisplayField) =>
  Boolean(field.type && field.type !== 'text' && field.type !== 'row')

const resolveFieldComponent = (field: CalendarEventDisplayField) => {
  const type = field.type
  if (!type || type === 'text' || type === 'row') {
    return type
  }

  return mergedFieldComponents.value[type] || type
}

const getFieldProps = (
  event: CalendarEvent,
  field: CalendarEventDisplayField,
) => {
  const fieldProps = {
    ...(field.props || {}),
  } as Record<string, unknown>

  if (field.key) {
    const value = getEventFieldValue(event, field.key)

    if (fieldProps.value === undefined) {
      fieldProps.value = value
    }

    if (fieldProps.modelValue === undefined) {
      fieldProps.modelValue = value
    }
  }

  if (fieldProps.event === undefined) {
    fieldProps.event = event
  }

  return fieldProps
}

const getRowProps = (field: CalendarEventDisplayField) => ({
  ...(field.props || {}),
})

const getRowColProps = (field: CalendarEventDisplayField) => ({
  span: 24,
  ...(field.colProps || {}),
})

const editableDisplayFields = computed(() =>
  resolvedEventDisplayFields.value.filter(
    (field) => Boolean(field.key) && Boolean(field.editComponent),
  ),
)

const editFormColumns = computed(() => {
  const fixedColumns = [
    {
      key: 'title',
      label: '日程名称',
      type: 'input',
      spanCol: 24,
      placeholder: '请输入日程名称',
      allowClear: true,
    },
    {
      key: 'timeRange',
      label: '时间',
      type: 'date-range',
      spanCol: 24,
      showTime: true,
      allowClear: false,
      valueFormat: 'YYYY-MM-DD HH:mm:ss',
    },
  ]

  const dynamicColumns = editableDisplayFields.value.map((field) => {
    const editComponent = field.editComponent || {}

    return {
      key: field.key,
      label: editComponent.label || field.label || field.key,
      type: editComponent.type || 'input',
      spanCol: editComponent.spanCol || 24,
      ...(editComponent.props || {}),
      colOptions: editComponent.colProps,
    }
  })

  return [...fixedColumns, ...dynamicColumns]
})

// 展示字段读取器：兼容数组/日期/字符串日期，减少模板层分支判断。
const getEventFieldValue = (event: CalendarEvent, key?: string) => {
  if (!key || !event) {
    return ''
  }

  const value = event[key]

  if (value === undefined || value === null || value === '') {
    return ''
  }

  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(' / ')
  }

  if (value instanceof Date) {
    return formatEventDateTime(value)
  }

  if (typeof value === 'string') {
    const date = toDateValue(value)
    if (
      date &&
      !Number.isNaN(date.getTime()) &&
      /\d{4}-\d{2}-\d{2}/.test(value)
    ) {
      return formatEventDateTime(date)
    }
  }

  return String(value)
}

const getEventTitle = (event?: CalendarEvent | null) => {
  if (!event) {
    return ''
  }

  return (
    getEventFieldValue(event, props.eventTitleKey) || String(event.title || '')
  )
}

/**
 * 将 hex 颜色转换为带透明度的 rgba 字符串
 * 不支持的格式原样返回（兜底）
 */
const hexToAlphaColor = (color: string, alpha: number): string => {
  if (!color) return color
  let hex = color.trim()
  if (hex.startsWith('#')) {
    hex = hex.slice(1)
    if (hex.length === 3)
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('')
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      if (!Number.isNaN(r) && !Number.isNaN(g) && !Number.isNaN(b)) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
    }
  }
  return color
}

/** 计算日程左边框颜色（borderColor > eventColor > 默认） */
const getEventBorderLeftColor = (event: CalendarEvent): string | undefined => {
  return event.borderColor || event.eventColor || undefined
}

/** 计算日程背景色（backgroundColor > eventColor浅色 > 默认） */
const getEventBgColor = (event: CalendarEvent): string | undefined => {
  if (event.backgroundColor) return event.backgroundColor
  if (event.eventColor) return hexToAlphaColor(event.eventColor, 0.12)
  return undefined
}

/**
 * 月视图事件：计算 CSS 自定义属性内联样式（作用于外层 .vuecal__event 元素）
 * 通过 CSS var 覆盖 SCSS 中的默认颜色
 */
const getMonthEventStyle = (event: CalendarEvent): Record<string, string> => {
  const style: Record<string, string> = {}
  const borderColor = getEventBorderLeftColor(event)
  const bgColor = getEventBgColor(event)
  if (borderColor) style['--ll-event-left-color'] = borderColor
  if (bgColor) style['--ll-event-bg-color'] = bgColor
  if (event.color) style['color'] = event.color
  return style
}

/**
 * 跨天行（周/日视图标题区域）的自定义样式
 * 仅作用于 start / single 段（有左边框的段）
 */
const getCrossDayRowStyle = (
  event: CalendarEvent,
  segment: 'start' | 'middle' | 'end' | 'single' | 'empty',
): Record<string, string> => {
  const style: Record<string, string> = {}
  const borderColor = getEventBorderLeftColor(event)
  const bgColor = getEventBgColor(event)
  if (bgColor) style['backgroundColor'] = bgColor
  if (borderColor && (segment === 'start' || segment === 'single')) {
    style['borderLeftColor'] = borderColor
  }
  if (event.color) style['color'] = event.color
  return style
}
const getTimedEventSkinStyle = (
  event: CalendarEvent,
): Record<string, string> => {
  const style: Record<string, string> = {}
  const borderColor = getEventBorderLeftColor(event)
  const bgColor = getEventBgColor(event)
  if (borderColor) style['borderLeftColor'] = borderColor
  if (bgColor) style['backgroundColor'] = bgColor
  return style
}

const getMonthCellEventClass = (event: CalendarEvent, cell: any) => {
  const cellDate = toDateValue(cell?.start)
  const eventStart = toDateValue(event.start)
  const eventEnd = toDateValue(event.end) || eventStart

  if (!cellDate || !eventStart || !eventEnd) {
    return ['ll-calendar__event-multiday-single']
  }

  const cellDay = dayjs(cellDate).startOf('day')
  const startDay = dayjs(eventStart).startOf('day')
  // 结束时间按开区间处理，避免 00:00 落到下一天导致段位判断偏移。
  const effectiveEnd = new Date(
    Math.max(eventEnd.getTime() - 1, eventStart.getTime()),
  )
  const endDay = dayjs(effectiveEnd).startOf('day')
  const isCrossDay = endDay.isAfter(startDay)

  if (!isCrossDay) {
    return ['ll-calendar__event-multiday-single']
  }

  const classes: string[] = []
  const firstColumnWeekday = props.weekStartDay === 1 ? 1 : 0

  if (cellDay.isSame(startDay)) {
    classes.push('ll-calendar__event-multiday-start')
    return classes
  }

  if (cellDay.isSame(endDay)) {
    classes.push('ll-calendar__event-multiday-end')
    if (cellDay.day() === firstColumnWeekday) {
      classes.push('ll-calendar__event-first-column-title')
    }
    return classes
  }

  classes.push('ll-calendar__event-multiday-middle')
  if (cellDay.day() === firstColumnWeekday) {
    classes.push('ll-calendar__event-first-column-title')
  }
  return classes
}

const getEventTimeLabel = (event: CalendarEvent) => {
  const startDate = toDateValue(event.start)
  if (!startDate || Number.isNaN(startDate.getTime())) {
    return ''
  }

  const endDate = toDateValue(event.end)
  const startLabel = formatEventTime(startDate)
  if (!endDate || Number.isNaN(endDate.getTime())) {
    return startLabel
  }

  return `${startLabel} - ${formatEventTime(endDate)}`
}

const canCreateAtRange = (
  startValue: string | Date | undefined,
  endValue?: string | Date,
) => {
  if (!props.disableCreateInPast) {
    return true
  }

  const start = toDateValue(startValue)
  const end = toDateValue(endValue)
  const nowTime = Date.now()

  if (start && !Number.isNaN(start.getTime()) && start.getTime() < nowTime) {
    return false
  }

  if (end && !Number.isNaN(end.getTime()) && end.getTime() < nowTime) {
    return false
  }

  return true
}

// 编辑面板与事件增删改逻辑拆分到 composable。

// 提交事件时只在“真实变化”时回写，避免无意义 update:modelValue 触发链路。
const getCommittedEvents = () => normalizeCalendarEvents(internalEvents.value)

const commitEvents = (events: CalendarEvent[]) => {
  const normalizedEvents = normalizeCalendarEvents(events)
  const nextInternalEvents = toCalendarEvents(normalizedEvents)
  if (!isSameEvents(internalEvents.value, nextInternalEvents)) {
    internalEvents.value = nextInternalEvents
  }

  // 启用日期组时，回写前先把派生事件折叠回原始记录结构，
  // 保证 modelValue 始终维持「一条记录一行」的存储形态。
  const outputEvents = isDateGroupMode.value
    ? foldDateGroupEvents(
        normalizedEvents,
        baseRecords.value,
        enabledDateGroups.value,
      )
    : normalizedEvents

  if (isDateGroupMode.value) {
    baseRecords.value = outputEvents.map((record) => ({ ...record }))
  }

  if (isSameEvents(props.modelValue, outputEvents)) {
    return
  }

  // 标记本次对外 emit 的内容，供 modelValue watch 识别并跳过自身回写
  lastCommittedOutput = outputEvents
  emit('update:modelValue', outputEvents)
  emit('change', outputEvents)
}

// 将拖拽/缩放后的单条事件补丁写回 internalEvents。
const patchEvent = (
  nextEvent: CalendarEvent,
  previousEvent?: CalendarEvent,
) => {
  const normalizedNextEvent = toCalendarEvent(nextEvent)
  const identifiers = new Set(
    [
      getEventIdentifier(previousEvent),
      getEventIdentifier(normalizedNextEvent),
    ].filter(Boolean),
  )

  let replaced = false

  const nextEvents = internalEvents.value.map((event) => {
    if (identifiers.has(getEventIdentifier(event))) {
      replaced = true
      return {
        ...event,
        ...normalizedNextEvent,
      }
    }

    return event
  })

  return replaced ? nextEvents : [...nextEvents, normalizedNextEvent]
}

const {
  editPanelRef,
  editPopoverOpen,
  editPresentationMode,
  editPopoverPlacement,
  contextMenuPanelRef,
  contextMenuOpen,
  contextMenuEvent,
  contextMenuStyle,
  editPopoverStyle,
  editBoundaryStyle,
  editFormData,
  isCreatingEvent,
  editingEventTitle,
  closeContextMenu,
  openContextMenu,
  openEditPopover,
  createDraftEventAndOpenEditor,
  openCreateEventDrawer,
  handleSwitchPresentation,
  handleEventDragCreate,
  deleteEvent,
  copyEvent,
  handleDeleteEditEvent,
  handleMaskClose,
  handleConfirmEdit,
  handleConfirmAndAdd,
  newDraftEventIdRef,
} = useCalendarEditor({
  props,
  emit: emit as any,
  defaultEventTitle: DEFAULT_EVENT_TITLE,
  slotStepMinutes,
  editableDisplayFields,
  internalEvents,
  currentEditableEvent,
  canQuickCreate,
  canSelectCreate,
  getEventIdentifier,
  commitEvents,
  patchEvent,
  canCreateAtRange,
  calendarContainerRef,
})

const bindEditPanelRef = (instance: unknown) => {
  editPanelRef.value = instance as typeof editPanelRef.value
}

const bindContextMenuPanelRef = (instance: unknown) => {
  contextMenuPanelRef.value = instance as typeof contextMenuPanelRef.value
}

const openMonthEventListModal = (
  cell: { start?: unknown; events?: unknown },
  nativeEvent: MouseEvent,
) => {
  selectedEventListDate.value =
    toDateValue(cell?.start as string | Date | undefined) || new Date()
  selectedEventListEvents.value = getSortedMonthCellEvents(cell?.events)
  eventListPanelStyle.value = calcEventListPanelPosition(nativeEvent)
  eventListModalOpen.value = true
}

const openHeaderMoreEventsModal = (
  dateValue: string | Date,
  events: CalendarEvent[],
  nativeEvent: MouseEvent,
) => {
  selectedEventListDate.value =
    toDateValue(dateValue as string | Date | undefined) || new Date()
  selectedEventListEvents.value = [...events]
  eventListPanelStyle.value = calcEventListPanelPosition(nativeEvent)
  eventListModalOpen.value = true
}

const handleEventListItemClick = (payload: {
  event: CalendarEvent
  nativeEvent: MouseEvent
}) => {
  handleNativeEventClick({
    event: payload.event,
    e: payload.nativeEvent,
  })
}

const handleEventListItemContextmenu = (payload: {
  event: CalendarEvent
  nativeEvent: MouseEvent
}) => {
  handleEventContextmenu({
    event: payload.event,
    nativeEvent: payload.nativeEvent,
  })
}

const isGoingToToday = ref(false)
const suppressViewDateSync = ref(false)

// 定位到当前时间（仅在 day/week 视图有效）
const scrollToNow = async () => {
  await nextTick()
  const calendar = calendarRef.value
  if (!calendar) {
    return
  }

  const now = new Date()
  const minutesNow =
    now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60
  if (minutesNow <= timeFromMinutes.value + 1) {
    const hostEl = calendarContainerRef.value
    const scrollableEl = hostEl?.querySelector(getViewScrollableSelector())

    if (scrollableEl instanceof HTMLElement) {
      scrollableEl.scrollTop = 0
      return
    }
  }

  // VueCal 提供 scrollToNow 方法来定位到当前时间
  calendar.scrollToNow?.()
}

const handleGoToToday = (viewNavigator: any) => {
  const today = new Date()
  internalCurrentDate.value = today
  selectedDate.value = today
  isGoingToToday.value = true
  viewNavigator?.goToToday?.()
}

const handleHeaderDateChange = (value: Dayjs | string | null) => {
  if (!value) {
    return
  }

  const nextDate = typeof value === 'string' ? new Date(value) : value.toDate()

  if (Number.isNaN(nextDate.getTime())) {
    return
  }

  internalCurrentDate.value = nextDate
  selectedDate.value = nextDate
}

const getViewAnchorDate = (view: any) => {
  const candidate =
    view?.date ?? view?.viewDate ?? view?.start ?? view?.extendedStart
  const parsedDate = toDateValue(candidate)

  return parsedDate && !Number.isNaN(parsedDate.getTime())
    ? parsedDate
    : new Date()
}

// 点击空白单元格：对外抛出 date-click，让业务侧决定是否创建事件。
const handleCellClick = (raw: any) => {
  if (props.readonly) {
    return
  }

  const date = raw?.cell?.start ?? raw?.cursor?.date ?? new Date()

  // 记录用户明确点击的日期，供视图模式切换时用作锚点
  const clickedDate = toDateValue(date)
  if (clickedDate && !Number.isNaN(clickedDate.getTime())) {
    selectedDate.value = clickedDate
  }

  emit('date-click', {
    raw: { date },
    events: getCommittedEvents(),
  })

  // 月视图单击可快速创建事件；周/日视图仍保持原交互（拖拽创建）。
  if (currentViewId.value !== 'month' || !canQuickCreate.value) {
    return
  }

  tryCreateFromMonthCellDate(
    toDateValue(date) || new Date(),
    raw?.cell?.schedule,
  )
}

const tryCreateFromMonthCellDate = (
  baseDate: Date,
  schedule?: string | number,
) => {
  // 月视图：默认从当日 00:00 到次日 00:00，视觉上恰好占一格
  const start = new Date(baseDate)
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 1)

  // 月视图仅检查日期部分是否早于当日（不考虑时间）
  if (props.disableCreateInPast) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (start.getTime() < today.getTime()) {
      return
    }
  }

  const createdEvent: CalendarEvent = {
    start,
    end,
    split: schedule,
  }

  createDraftEventAndOpenEditor(createdEvent, schedule)
}

const isMonthCellInPast = (cellStart: unknown) => {
  if (!props.disableCreateInPast) return false
  const cellDate = toDateValue(cellStart as string | Date | undefined)
  if (!cellDate) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const cellDay = new Date(cellDate)
  cellDay.setHours(0, 0, 0, 0)
  return cellDay.getTime() < today.getTime()
}

const handleWeekHeadingCreateClick = (date: Date, mouseEvent: MouseEvent) => {
  mouseEvent.preventDefault()
  mouseEvent.stopPropagation()

  if (props.readonly || !canQuickCreate.value) {
    return
  }

  tryCreateFromMonthCellDate(date)
}

const handleMonthCellCreateClick = (
  cell: { start?: unknown; schedule?: string | number },
  mouseEvent: Event,
) => {
  if (mouseEvent instanceof MouseEvent) {
    mouseEvent.preventDefault()
    mouseEvent.stopPropagation()
  }

  if (props.readonly || !canQuickCreate.value) {
    return
  }

  const nextDate =
    toDateValue(cell?.start as string | Date | undefined) || new Date()
  tryCreateFromMonthCellDate(nextDate, cell?.schedule)
}

const handleContainerClickCapture = (event: MouseEvent) => {
  if (
    props.clickOutOfMonthJump ||
    currentViewId.value !== 'month' ||
    props.readonly
  ) {
    return
  }

  const target = event.target as HTMLElement | null
  if (target?.closest('.ll-calendar__month-cell-add-btn')) {
    return
  }
  const outOfRangeCell = target?.closest('.vuecal__cell--out-of-range')
  if (!outOfRangeCell) {
    return
  }

  const cellEl = outOfRangeCell as HTMLElement
  const startTimestamp = Number(cellEl.dataset.start)
  const clickedDate = Number.isFinite(startTimestamp)
    ? new Date(startTimestamp)
    : new Date()
  const scheduleValue = cellEl.dataset.schedule

  event.preventDefault()
  event.stopPropagation()

  preventOutOfMonthJumpPending.value = false
  monthAnchorBeforeOutOfMonthClick.value = null

  emit('date-click', {
    raw: { date: clickedDate },
    events: getCommittedEvents(),
  })

  if (!canQuickCreate.value) {
    return
  }

  tryCreateFromMonthCellDate(clickedDate, scheduleValue)
}

const handleContainerPointerDown = (event: Event) => {
  // 当 disableCreateInPast 开启时，阻止在周/日视图过去时间区域发起拖拽创建。
  // 通过在 capture 阶段拦截 mousedown 来阻止 vue-cal 内部的拖拽追踪逻辑启动，
  // 从而彻底消除拖拽视觉反馈（而非等 @event-created 后再删除事件）。
  // 仅处理 MouseEvent；touch 场景保留现有的 handleEventDragCreate 兜底逻辑。
  if (
    event instanceof MouseEvent &&
    props.disableCreateInPast &&
    canSelectCreate.value &&
    currentViewId.value !== 'month'
  ) {
    const target = event.target as HTMLElement | null
    // 点击已有事件时不阻止（允许打开编辑过去事件）
    if (!target?.closest('.vuecal__event')) {
      const cellEl = target?.closest<HTMLElement>('.vuecal__cell')
      if (cellEl) {
        const cellStartMs = parseInt(
          cellEl.getAttribute('data-start') ?? '',
          10,
        )
        if (!Number.isNaN(cellStartMs)) {
          const hostEl = calendarContainerRef.value
          const bodyEl = hostEl?.querySelector<HTMLElement>('.vuecal__body')
          if (bodyEl) {
            const bodyRect = bodyEl.getBoundingClientRect()
            const relativeToGridTop = event.clientY - bodyRect.top
            if (relativeToGridTop >= 0) {
              // 与 now-line 公式相同：像素位置 → 从午夜起算的分钟数
              const clickedMinutes =
                timeFromMinutes.value +
                (relativeToGridTop / props.timeCellHeight) *
                  slotStepMinutes.value
              // cellStartMs 是该天午夜的 epoch ms
              const clickedMs = cellStartMs + clickedMinutes * 60 * 1000
              if (clickedMs < Date.now()) {
                event.stopPropagation()
                return
              }
            }
          }
        }
      }
    }
  }

  if (props.clickOutOfMonthJump || currentViewId.value !== 'month') {
    preventOutOfMonthJumpPending.value = false
    monthAnchorBeforeOutOfMonthClick.value = null
    return
  }

  const target = event.target as HTMLElement | null
  if (target?.closest('.ll-calendar__month-cell-add-btn')) {
    preventOutOfMonthJumpPending.value = false
    monthAnchorBeforeOutOfMonthClick.value = null
    return
  }
  const outOfRangeCell = target?.closest('.vuecal__cell--out-of-range')

  if (!outOfRangeCell) {
    preventOutOfMonthJumpPending.value = false
    monthAnchorBeforeOutOfMonthClick.value = null
    return
  }

  preventOutOfMonthJumpPending.value = true
  monthAnchorBeforeOutOfMonthClick.value = new Date(internalCurrentDate.value)
}

// 事件点击与右键上下文菜单分离，便于业务层做不同交互。
const handleNativeEventClick = (payload: {
  event: CalendarEvent
  e?: Event
}) => {
  if (props.readonly) {
    return
  }

  const nativeMouseEvent = payload.e as MouseEvent | undefined
  if (nativeMouseEvent?.button === 2) {
    return
  }

  const normalizedEvent = normalizeCalendarEvent(payload.event)

  // 记录事件所在日期，供视图模式切换时用作锚点
  const eventDate = toDateValue(normalizedEvent.start)
  if (eventDate && !Number.isNaN(eventDate.getTime())) {
    selectedDate.value = eventDate
  }

  const rawClick: CalendarEventClickRaw = {
    event: normalizedEvent,
    nativeEvent: payload.e,
  }

  emit('event-click', {
    raw: rawClick,
    event: normalizedEvent,
    events: getCommittedEvents(),
  })

  openEditPopover(payload.event, '', nativeMouseEvent)
}

const handleEventContextmenu = (payload: {
  event: CalendarEvent
  e?: Event
  nativeEvent?: MouseEvent
}) => {
  if (props.readonly) {
    return
  }

  const nativeEvent =
    payload.nativeEvent ||
    ((payload.e as MouseEvent | undefined) instanceof MouseEvent
      ? (payload.e as MouseEvent)
      : undefined)

  if (!nativeEvent) {
    return
  }

  nativeEvent.preventDefault()
  nativeEvent.stopPropagation()

  const normalizedEvent = normalizeCalendarEvent(payload.event)
  emit('event-contextmenu', {
    raw: {
      event: normalizedEvent,
      nativeEvent,
    },
    event: normalizedEvent,
    events: getCommittedEvents(),
  })

  openContextMenu(payload.event, nativeEvent)
}

const handleContextMenuEdit = () => {
  if (props.readonly) {
    return
  }

  const targetEvent = contextMenuEvent.value
  closeContextMenu()
  if (!targetEvent) {
    return
  }

  const contextLeft = Number.parseFloat(contextMenuStyle.value.left || '')
  const contextTop = Number.parseFloat(contextMenuStyle.value.top || '')
  const anchorPoint =
    Number.isFinite(contextLeft) && Number.isFinite(contextTop)
      ? { x: contextLeft, y: contextTop }
      : undefined

  openEditPopover(targetEvent, '', anchorPoint)
}

const handleContextMenuCopy = () => {
  if (props.readonly) {
    return
  }

  const targetEvent = contextMenuEvent.value
  closeContextMenu()
  if (!targetEvent) {
    return
  }

  copyEvent(targetEvent)
}

const handleContextMenuDelete = () => {
  if (props.readonly) {
    return
  }

  const targetEvent = contextMenuEvent.value
  closeContextMenu()
  if (!targetEvent) {
    return
  }

  deleteEvent(targetEvent)
}

const scheduleInteractionCommit = (task: () => void) => {
  if (
    typeof window === 'undefined' ||
    typeof window.requestAnimationFrame !== 'function'
  ) {
    task()
    return
  }

  // 等待 vue-cal 在当前交互帧内完成内部状态清理，再回写外部 events。
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      task()
    })
  })
}

// ── 月视图自定义拖拽 ──────────────────────────────────────────────────────────
// 月视图使用自定义 #cell 插槽渲染日程，无法依赖 vue-cal 原生拖拽，
// 通过 HTML5 DragEvent 自行实现日程在日期格之间的移动。

const monthDraggingEvent = ref<CalendarEvent | null>(null)
const monthDragOverCellDate = ref<Date | null>(null)

const canDragMonthEvent = computed(
  () => !props.readonly && currentEditableEvent.value.drag !== false,
)

const isSameDayCell = (a: Date, b: Date) => dayjs(a).isSame(dayjs(b), 'day')

const handleMonthEventDragStart = (event: CalendarEvent, e: DragEvent) => {
  monthDraggingEvent.value = event
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', getEventIdentifier(event))
  }
}

const handleMonthEventDragEnd = () => {
  monthDraggingEvent.value = null
  monthDragOverCellDate.value = null
}

// handleMonthCellDragEnter：拦截冒泡（阻止 vue-cal 的 2 秒视图切换 timer），
// 并更新悬停高亮格。
const handleMonthCellDragEnter = (cellDate: Date, e: DragEvent) => {
  if (!monthDraggingEvent.value) return
  // 阻止冒泡到 vue-cal 的 cellDragEnter（内含 2s 切换视图 timer）
  e.stopPropagation()
  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = 'move'
  }
  monthDragOverCellDate.value = cellDate
}

const handleMonthCellDragOver = (cellDate: Date, e: DragEvent) => {
  if (!monthDraggingEvent.value) return
  // preventDefault 告知浏览器允许 drop；stopPropagation 阻止 vue-cal 的 cellDragOver
  e.preventDefault()
  e.stopPropagation()
  if (e.dataTransfer) {
    e.dataTransfer.dropEffect = 'move'
  }
  if (
    !monthDragOverCellDate.value ||
    !isSameDayCell(monthDragOverCellDate.value, cellDate)
  ) {
    monthDragOverCellDate.value = cellDate
  }
}

const handleMonthCellDragLeave = (cellDate: Date, e: DragEvent) => {
  if (!monthDraggingEvent.value) return
  e.stopPropagation()
  // 仅当鼠标离开包裹层（不是进入子元素）时才清除高亮
  const wrapper = e.currentTarget as Element | null
  const relatedTarget = e.relatedTarget as Node | null
  if (wrapper && wrapper.contains(relatedTarget)) return
  if (
    monthDragOverCellDate.value &&
    isSameDayCell(monthDragOverCellDate.value, cellDate)
  ) {
    monthDragOverCellDate.value = null
  }
}

const handleMonthCellDrop = (cellDate: Date, e: DragEvent) => {
  // 不是我们自己发起的月视图拖拽，让事件继续冒泡给 vue-cal 处理（外部拖拽等）
  if (!monthDraggingEvent.value) return
  // 阻止冒泡到 vue-cal 的 cellDragDrop（会把自定义拖拽误判为外部事件新建日程）
  e.preventDefault()
  e.stopPropagation()

  const draggedEvent = monthDraggingEvent.value
  monthDraggingEvent.value = null
  monthDragOverCellDate.value = null

  if (props.readonly) return

  // 拖到同一天无需处理
  if (isSameDayCell(dayjs(draggedEvent.start).toDate(), cellDate)) return

  const originalEvent = { ...draggedEvent }
  const startDayjs = dayjs(draggedEvent.start)
  const endDayjs = draggedEvent.end
    ? dayjs(draggedEvent.end)
    : startDayjs.add(1, 'hour')
  const durationMs = endDayjs.diff(startDayjs)

  const targetDay = dayjs(cellDate).startOf('day')

  // 禁止拖拽到过去日期
  if (isMonthCellInPast(targetDay.toDate())) return

  const newStart = targetDay
    .hour(startDayjs.hour())
    .minute(startDayjs.minute())
    .second(startDayjs.second())
    .millisecond(0)
  const newEnd = newStart.add(durationMs, 'millisecond')

  const updatedEvent: CalendarEvent = {
    ...draggedEvent,
    start: newStart.toDate(),
    end: newEnd.toDate(),
  }

  const normalizedEvent = normalizeCalendarEvent(updatedEvent)
  const nextEvents = patchEvent(normalizedEvent, originalEvent)

  // 月视图不经过 vue-cal 内部拖拽流程，无需等待帧清理，直接同步提交
  // 避免双 RAF 延迟导致事件短暂闪回原位。
  commitEvents(nextEvents)

  emit('event-drop', {
    raw: {
      event: normalizedEvent,
      originalEvent: normalizeCalendarEvent(originalEvent),
      external: false,
    },
    event: normalizedEvent,
    events: normalizeCalendarEvents(nextEvents),
  })
}

const handleEventDrop = (raw: {
  event: CalendarEvent
  originalEvent?: CalendarEvent
  external?: boolean
}) => {
  if (props.readonly) {
    return
  }

  const normalizedEvent = normalizeCalendarEvent(raw.event)

  // 禁止拖拽到过去时间：强制回滚到原始位置
  if (!canCreateAtRange(normalizedEvent.start, normalizedEvent.end)) {
    scheduleInteractionCommit(() => {
      internalEvents.value = [...internalEvents.value]
    })
    return
  }

  const nextEvents = patchEvent(normalizedEvent, raw.originalEvent)
  scheduleInteractionCommit(() => {
    commitEvents(nextEvents)

    emit('event-drop', {
      raw: {
        event: normalizedEvent,
        originalEvent: raw.originalEvent
          ? normalizeCalendarEvent(raw.originalEvent)
          : undefined,
        external: raw.external,
      },
      event: normalizedEvent,
      events: normalizeCalendarEvents(nextEvents),
    })
  })
}

// 调整时长与拖拽移动都走同一套 patch + commit 流程，保持回写行为一致。
const handleEventDurationChange = (raw: {
  event: CalendarEvent
  original?: CalendarEvent
}) => {
  if (props.readonly) {
    return
  }

  const normalizedEvent = normalizeCalendarEvent(raw.event)

  // 禁止调整到过去时间：强制回滚到原始位置
  if (!canCreateAtRange(normalizedEvent.start, normalizedEvent.end)) {
    scheduleInteractionCommit(() => {
      internalEvents.value = [...internalEvents.value]
    })
    return
  }

  const nextEvents = patchEvent(normalizedEvent, raw.original)
  scheduleInteractionCommit(() => {
    commitEvents(nextEvents)

    emit('event-resize', {
      raw: {
        event: normalizedEvent,
        originalEvent: raw.original
          ? normalizeCalendarEvent(raw.original)
          : undefined,
      },
      event: normalizedEvent,
      events: normalizeCalendarEvents(nextEvents),
    })
  })
}

// vue-cal 的 view-change/ready 都统一转换成 dates-change，确保对外事件模型一致。
const emitDatesChange = (raw: CalendarDatesChangeRaw) => {
  currentViewRange.value = {
    start: toDateValue(raw.startDate) || new Date(),
    end: toDateValue(raw.endDate) || new Date(),
  }
  currentViewId.value = raw.view
  renderedViewId.value = raw.view
  emit('update:view', raw.view)
  emit('dates-change', {
    raw,
    events: normalizeCalendarEvents(raw.events),
  })
}

const handleViewChange = async (view: any) => {
  const nextViewId = (view?.id ?? currentViewId.value) as CalendarView

  if (
    nextViewId === 'month' &&
    !props.clickOutOfMonthJump &&
    preventOutOfMonthJumpPending.value
  ) {
    const anchor = monthAnchorBeforeOutOfMonthClick.value
    preventOutOfMonthJumpPending.value = false
    monthAnchorBeforeOutOfMonthClick.value = null

    if (anchor && !Number.isNaN(anchor.getTime())) {
      internalCurrentDate.value = new Date(anchor)
      return
    }
  }

  preventOutOfMonthJumpPending.value = false
  monthAnchorBeforeOutOfMonthClick.value = null

  const isSwitchingViewMode = nextViewId !== renderedViewId.value
  const explicitViewDate = toDateValue(view?.date ?? view?.viewDate)

  if (isGoingToToday.value) {
    isGoingToToday.value = false
    // goToToday 触发的 view-change 不覆盖已设置好的今日日期
  } else if (isSwitchingViewMode) {
    // 切换视图模式时，优先以用户最后选中的日期为锚点。
    // selectedDate 会在单元格点击、事件点击、日期选择器及同视图翻页时更新，
    // 保证 月→周/日、周→日 等任意方向切换都能"定位到上次选中的位置"。
    headerEventsExpanded.value = false
    const anchorForSwitch = selectedDate.value || internalCurrentDate.value
    internalCurrentDate.value = anchorForSwitch
    suppressViewDateSync.value = true
    requestAnimationFrame(() => {
      suppressViewDateSync.value = false
    })
  } else if (suppressViewDateSync.value) {
    // 忽略视图切换后紧跟的内部同步事件，防止日期连续回退。
  } else if (explicitViewDate && !Number.isNaN(explicitViewDate.getTime())) {
    // 优先使用 vue-cal 提供的显式焦点日期。
    internalCurrentDate.value = explicitViewDate
    selectedDate.value = explicitViewDate
  } else {
    // 同一视图内翻页时，允许使用区间锚点更新当前日期。
    const anchorDate = getViewAnchorDate(view)
    internalCurrentDate.value = anchorDate
    selectedDate.value = anchorDate
  }
  emitDatesChange(mapViewToRaw(view))

  // 切换到 day/week 视图时，如果当前日期是今天，自动定位到当前时间
  if (
    (nextViewId === 'day' || nextViewId === 'week') &&
    isToday(internalCurrentDate.value)
  ) {
    await scrollToNow()
  }

  scheduleTimeCellWidthSync()
  scheduleWeekHeadingsHeightSync()
  scheduleTransientScrollbarListenerSync()
  syncMonthCellHeight()

  // 视图切换后再更新时间线，给 DOM 更多时间完全就绪
  if (nextViewId === 'day' || nextViewId === 'week') {
    await new Promise((resolve) => setTimeout(resolve, 300))
    customNowLineVisible.value = false
    if (customNowLineScrollableEl) {
      customNowLineScrollableEl.removeEventListener(
        'scroll',
        updateCustomNowLinePosition,
      )
      customNowLineScrollableEl = null
    }
    scheduleCustomNowLinePositionSync(true)
  }
}

const handleReady = ({ view }: { config: any; view: any }) => {
  const anchorDate = getViewAnchorDate(view)
  internalCurrentDate.value = anchorDate
  // 初始时若外部传入了 currentDate，selectedDate 已在声明时初始化；
  // 否则以 VueCal 提供的锚点（通常为今天）作为默认选中日期。
  if (!selectedDate.value) {
    selectedDate.value = anchorDate
  }
  emitDatesChange(mapViewToRaw(view))

  // 初始加载时，如果视图是 day/week 且当前日期是今天，自动定位到当前时间
  const viewId = (view?.id ?? props.view) as CalendarView
  if (
    (viewId === 'day' || viewId === 'week') &&
    isToday(internalCurrentDate.value)
  ) {
    scrollToNow()
  }

  scheduleTimeCellWidthSync()
  scheduleWeekHeadingsHeightSync()
  scheduleCustomNowLinePositionSync()
  scheduleTransientScrollbarListenerSync()
  syncMonthCellHeight()
}

// 用于 addEventListener/removeEventListener 的包装函数
const handleResizeForCustomNowLine = () => scheduleCustomNowLinePositionSync()

onMounted(() => {
  window.addEventListener('resize', scheduleTimeCellWidthSync)
  window.addEventListener('resize', scheduleWeekHeadingsHeightSync)
  window.addEventListener('resize', handleResizeForCustomNowLine)
  window.addEventListener('resize', scheduleTransientScrollbarListenerSync)
  scheduleTimeCellWidthSync()
  scheduleWeekHeadingsHeightSync()

  // VueCal 的 watch-real-time 已自动更新 now 状态（对齐整分钟），此处只做初始滚动定位
  scrollToNow()
  startCustomNowLineTicker()
  scheduleTransientScrollbarListenerSync()
  syncMonthCellHeight()
})

watch(
  () => props.view,
  (value) => {
    // 仅在 watch 中更新 currentViewId，其他逻辑由 handleViewChange 处理
    currentViewId.value = value
    headerEventsExpanded.value = false
    scheduleWeekHeadingsHeightSync()
    startCustomNowLineTicker()
  },
)

watch(
  () => weekCrossDayLayout.value.totalRows,
  () => {
    scheduleTransientScrollbarListenerSync()
  },
)

watch(
  () => props.currentDate,
  (value) => {
    const nextDate = toDateValue(value)

    if (!nextDate || Number.isNaN(nextDate.getTime())) {
      return
    }

    internalCurrentDate.value = nextDate
    selectedDate.value = nextDate
  },
)

watch(
  () => props.modelValue,
  (value) => {
    // 跳过组件自身 commitEvents 刚刚 emit 出去、又被父级 v-model 同步回写的内容：
    // 此时 internalEvents 已是最新，无需再次按日期组展开（fold/expand 往返不保证
    // 逐字节一致，重复展开会导致 internalEvents ⇄ modelValue 反复改写而递归更新）。
    if (lastCommittedOutput && isSameEvents(value ?? [], lastCommittedOutput)) {
      lastCommittedOutput = null
      return
    }
    lastCommittedOutput = null

    // 外部 v-model 变化时同步到内部；启用日期组时先按组展开。
    baseRecords.value = (value ?? []).map((record) => ({ ...record }))
    const normalizedValue = toCalendarEvents(expandRecords(value ?? []))

    if (isSameEvents(internalEvents.value, normalizedValue)) {
      return
    }

    internalEvents.value = normalizedValue
    scheduleWeekHeadingsHeightSync()
  },
  { deep: true },
)

// 日期组配置变化时（含启用/禁用、字段调整、标题模板），重新展开显示层事件。
watch(
  () => [props.dateGroups, props.dateGroupTitleFormat],
  () => {
    baseRecords.value = (props.modelValue ?? []).map((record) => ({
      ...record,
    }))
    const normalizedValue = toCalendarEvents(expandRecords(props.modelValue ?? []))
    if (isSameEvents(internalEvents.value, normalizedValue)) {
      return
    }
    internalEvents.value = normalizedValue
    scheduleWeekHeadingsHeightSync()
  },
  { deep: true },
)


watch(
  () => [
    shouldShowCustomNowLine.value,
    isWatchRealTimeEnabled.value,
    props.timeCellHeight,
    timeFromMinutes.value,
    timeToMinutes.value,
    slotStepMinutes.value,
  ],
  () => {
    startCustomNowLineTicker()
  },
)

watch(
  () => editPopoverOpen.value,
  (open) => {
    document.body.style.overflow = open ? 'hidden' : ''
  },
)

onBeforeUnmount(() => {
  document.body.style.overflow = ''
  window.removeEventListener('resize', scheduleTimeCellWidthSync)
  window.removeEventListener('resize', scheduleWeekHeadingsHeightSync)
  window.removeEventListener('resize', handleResizeForCustomNowLine)
  window.removeEventListener('resize', scheduleTransientScrollbarListenerSync)
  cleanupWeekHeadingsObservers()
  cleanupMonthCellObserver()
  stopCustomNowLineTicker()

  clearTransientScrollbarTimer()
  if (transientScrollbarScrollableEl) {
    transientScrollbarScrollableEl.removeEventListener(
      'scroll',
      handleTransientScrollbarScroll,
    )
    transientScrollbarScrollableEl = null
  }
})

defineExpose({
  getCalendar: () => calendarRef.value,
  addEvent: openCreateEventDrawer,
  openSidebar: openCreateEventDrawer,
  editEvent: openEditPopover,
  deleteEvent,
  copyEvent,
})
</script>
